import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  FiActivity,
  FiAtSign,
  FiBarChart2,
  FiBold,
  FiBriefcase,
  FiCheck,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronDown,
  FiChevronRight,
  FiClock,
  FiColumns,
  FiCopy,
  FiDroplet,
  FiEdit3,
  FiFileText,
  FiHash,
  FiImage,
  FiItalic,
  FiList,
  FiGrid,
  FiLink,
  FiLoader,
  FiMaximize2,
  FiMessageCircle,
  FiMoreHorizontal,
  FiPaperclip,
  FiPlus,
  FiSearch,
  FiSmile,
  FiTrendingUp,
  FiTrash2,
  FiUser,
  FiUsers,
  FiX,
} from "react-icons/fi";
import ComponentLoader from "../components/ComponentLoader";
import ScreenHeader from "./ScreenHeader";
import { addProjectSentiment, getProjectDetail } from "../api/hrmApi";

const STATUS_COLUMNS = [
  { id: "todo", label: "To Do", status: "Defined", matches: ["draft", "defined", "ready", "open", "todo", "to do", "new"] },
  { id: "progress", label: "In Progress", status: "In Progress", matches: ["in progress", "progress", "development", "working"] },
  { id: "review", label: "Review", status: "Review", matches: ["review", "qa", "testing"] },
  { id: "blocked", label: "Blocked", status: "Blocked", matches: ["blocked", "hold"] },
  { id: "done", label: "Done", status: "Closed", matches: ["closed", "done", "prod", "demoed", "complete", "completed"] },
];

const STATUS_OPTIONS = ["Defined", "Ready", "In Progress", "Review", "Blocked", "Prod", "Closed"];
const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Critical"];
const PROJECT_EMOJIS = ["👍", "✅", "🔥", "🚀", "🎯", "👀", "💡", "⚠️", "🙌", "🙏"];
const PROJECT_TEXT_COLORS = ["#172B4D", "#2454E8", "#0E7A3A", "#B42318", "#B54708", "#6941C6"];
const PROJECT_MENTION_CLASS = "new-hrm-project-comment-mention-pill";
const PROJECT_TABLE_MIN_SIZE = 1;
const PROJECT_TABLE_MAX_SIZE = 12;

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const MONTH_LOOKUP = {
  jan: "01",
  january: "01",
  feb: "02",
  february: "02",
  mar: "03",
  march: "03",
  apr: "04",
  april: "04",
  may: "05",
  jun: "06",
  june: "06",
  jul: "07",
  july: "07",
  aug: "08",
  august: "08",
  sep: "09",
  sept: "09",
  september: "09",
  oct: "10",
  october: "10",
  nov: "11",
  november: "11",
  dec: "12",
  december: "12",
};

const getColumnId = (status) => {
  const normalized = normalizeText(status);
  return STATUS_COLUMNS.find((column) => column.matches.some((match) => normalized === match || normalized.includes(match)))?.id || "todo";
};

const toMonthValue = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return "";
  }

  const isoMatch = rawValue.match(/^(\d{4})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}`;
  }

  const shortDateMatch = rawValue.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  const matchedMonth = shortDateMatch ? MONTH_LOOKUP[shortDateMatch[2].toLowerCase()] : "";
  if (matchedMonth) {
    return `${shortDateMatch[3]}-${matchedMonth}`;
  }

  const parsedDate = new Date(rawValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    return `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, "0")}`;
  }

  return "";
};

const getEntityMonthValue = (entity) => toMonthValue(entity?.dueDate) || toMonthValue(entity?.closeDate);

const formatMonthLabel = (monthValue) => {
  const [year, month] = String(monthValue || "").split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (!year || !month || Number.isNaN(date.getTime())) {
    return monthValue;
  }

  return new Intl.DateTimeFormat("en", { month: "short", year: "numeric" }).format(date);
};

const getMonthValueFromDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const getProjectMonthOptions = (entities = []) => {
  const months = new Set();
  const today = new Date();
  months.add(getMonthValueFromDate(today));

  for (let offset = -3; offset <= 3; offset += 1) {
    const optionDate = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    months.add(getMonthValueFromDate(optionDate));
  }

  entities.forEach((entity) => {
    const monthValue = getEntityMonthValue(entity);
    if (monthValue) {
      months.add(monthValue);
    }
  });

  return Array.from(months).sort().reverse();
};

const getInitials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "NA";

const toTone = (value) =>
  normalizeText(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "neutral";

const normalizeProjectColor = (value) => {
  const color = String(value || "").trim();
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(color)) {
    return color;
  }
  if (/^(rgb|rgba|hsl|hsla)\([\d\s,%.+-]+\)$/.test(color)) {
    return color;
  }
  return "#2D58ED";
};

const isValidProjectHexColor = (value) => /^#[0-9a-fA-F]{6}$/.test(String(value || "").trim());

const escapeRegExp = (value = "") => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const isSafeRichTextUrl = (value = "") => /^(https?:|mailto:|tel:)/i.test(String(value || "").trim());

const normalizeRichTextUrl = (value = "") => {
  const url = String(value || "").trim();
  if (!url) {
    return "";
  }
  if (/^www\./i.test(url)) {
    return `https://${url}`;
  }
  return isSafeRichTextUrl(url) ? url : "";
};

const sanitizeRichTextColor = (value = "") => {
  const color = String(value || "").trim();
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color)) {
    return color.toUpperCase();
  }
  if (/^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)$/i.test(color)) {
    return color;
  }
  return "";
};

const buildProjectEntityLink = (entity = {}) => {
  const entityId = String(entity.id || entity.entityId || entity.taskId || entity.key || "").trim();
  if (!entityId) {
    return "";
  }
  if (typeof window === "undefined") {
    return `/HRM?tab=projects&entityId=${encodeURIComponent(entityId)}`;
  }
  const url = new URL(window.location.href);
  if (!/\/HRM\/?$/i.test(url.pathname)) {
    url.pathname = "/HRM";
  }
  url.searchParams.set("tab", "projects");
  url.searchParams.set("entityId", entityId);
  url.hash = "";
  return url.toString();
};

const formatProjectFileSize = (size = 0) => {
  const bytes = Number(size) || 0;
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(bytes / 1024, 1).toFixed(0)} KB`;
};

const fileExtensionFromType = (type = "") => {
  const normalizedType = String(type || "").toLowerCase();
  if (normalizedType.includes("png")) return "png";
  if (normalizedType.includes("webp")) return "webp";
  if (normalizedType.includes("gif")) return "gif";
  return "jpg";
};

const imageFilesFromClipboard = (event, prefix = "pasted-image") => {
  const items = Array.from(event.clipboardData?.items || []);
  const files = items
    .map((item) => (item.kind === "file" && item.type.startsWith("image/") ? item.getAsFile() : null))
    .filter(Boolean);

  if (!files.length) {
    return [];
  }

  const timestamp = Date.now();
  return files.map((file, index) => {
    const hasUsableName = file.name && file.name !== "image.png";
    if (hasUsableName || typeof File === "undefined") {
      return file;
    }
    return new File([file], `${prefix}-${timestamp}-${index + 1}.${fileExtensionFromType(file.type)}`, {
      type: file.type,
      lastModified: timestamp,
    });
  });
};

export const stripProjectRichText = (value = "") => {
  const rawValue = decodeProjectRichText(String(value || ""));
  if (!rawValue) {
    return "";
  }
  const valueWithBreaks = rawValue
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n");
  if (typeof window !== "undefined" && window.DOMParser) {
    return new window.DOMParser().parseFromString(valueWithBreaks, "text/html").body.textContent
      .replace(/\s+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }
  return valueWithBreaks.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
};

const clampProjectTableSize = (value, fallback) => {
  const parsedValue = Number.parseInt(value, 10);
  if (!Number.isFinite(parsedValue)) {
    return fallback;
  }
  return Math.min(PROJECT_TABLE_MAX_SIZE, Math.max(PROJECT_TABLE_MIN_SIZE, parsedValue));
};

const createProjectTableElement = (rowCount = 3, columnCount = 3) => {
  const rows = clampProjectTableSize(rowCount, 3);
  const columns = clampProjectTableSize(columnCount, 3);
  const table = document.createElement("table");
  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");

  Array.from({ length: columns }).forEach((_, index) => {
    const cell = document.createElement("th");
    cell.textContent = `Column ${index + 1}`;
    headRow.appendChild(cell);
  });
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = document.createElement("tbody");
  Array.from({ length: Math.max(0, rows - 1) }).forEach(() => {
    const row = document.createElement("tr");
    Array.from({ length: columns }).forEach(() => {
      const cell = document.createElement("td");
      cell.appendChild(document.createElement("br"));
      row.appendChild(cell);
    });
    tbody.appendChild(row);
  });
  table.appendChild(tbody);
  return table;
};

const createProjectTableHtml = (rowCount = 3, columnCount = 3) => {
  if (typeof document === "undefined") {
    return "";
  }
  const container = document.createElement("div");
  container.appendChild(createProjectTableElement(rowCount, columnCount));
  return container.innerHTML;
};

const decodeProjectRichText = (value = "") => {
  const rawValue = String(value || "");
  if (!/&lt;\/?[a-z][\s\S]*?&gt;/i.test(rawValue)) {
    return rawValue;
  }

  if (typeof window === "undefined" || !window.DOMParser) {
    return rawValue
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, "\"")
      .replace(/&#39;/gi, "'")
      .replace(/&amp;/gi, "&");
  }

  return new window.DOMParser().parseFromString(rawValue, "text/html").body.textContent || rawValue;
};

const sanitizeProjectRichText = (value = "") => {
  const rawValue = decodeProjectRichText(value).trim();
  if (!rawValue || typeof window === "undefined" || !window.DOMParser) {
    return "";
  }

  const body = new window.DOMParser().parseFromString(rawValue.replace(/<br\s*\/?>/gi, "<br>"), "text/html").body;
  body.querySelectorAll("script,style,iframe,object,embed").forEach((node) => node.remove());
  body.querySelectorAll("*").forEach((node) => {
    const tagName = node.tagName?.toLowerCase();
    const safeColor = sanitizeRichTextColor(node.style?.color || node.getAttribute("color"));
    const safeHref = tagName === "a" ? normalizeRichTextUrl(node.getAttribute("href")) : "";
    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const valueText = attribute.value || "";
      const isSafeLink = tagName === "a" && name === "href" && isSafeRichTextUrl(valueText);
      const isMentionClass =
        name === "class" &&
        tagName === "span" &&
        valueText.split(/\s+/).includes(PROJECT_MENTION_CLASS);
      if (isMentionClass) {
        node.setAttribute("class", PROJECT_MENTION_CLASS);
      } else if (!isSafeLink) {
        node.removeAttribute(attribute.name);
      }
    });
    if (safeColor) {
      node.setAttribute("style", `color: ${safeColor};`);
    } else {
      node.removeAttribute("style");
    }
    node.removeAttribute("color");
    if (tagName === "a" && safeHref) {
      node.setAttribute("href", safeHref);
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noreferrer");
    }
  });

  return body.innerHTML;
};

const projectEditorHtml = (value = "") => {
  const rawValue = decodeProjectRichText(value).trim();
  if (!rawValue) {
    return "";
  }
  if (/<[a-z][\s\S]*>/i.test(rawValue)) {
    return sanitizeProjectRichText(rawValue);
  }
  return escapeHtml(rawValue).replace(/\n/g, "<br>");
};

export const ProjectRichText = ({ className = "", emptyText = "No content provided.", value = "" }) => {
  const rawValue = decodeProjectRichText(value).trim();
  const hasHtml = /<[a-z][\s\S]*>/i.test(rawValue);
  const sanitizedHtml = useMemo(() => sanitizeProjectRichText(rawValue), [rawValue]);

  if (!rawValue) {
    return <p className={className}>{emptyText}</p>;
  }

  if (hasHtml && sanitizedHtml) {
    return <div className={`new-hrm-project-rich-text ${className}`} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
  }

  return <p className={`new-hrm-project-rich-text ${className}`}>{rawValue}</p>;
};

export const ProjectCommentBody = ({ attachments = [], mentions = [], value = "" }) => {
  const rawValue = String(value || "").trim();
  const highlightedHtml = useMemo(() => {
    if (!rawValue) {
      return "";
    }

    const hasHtml = /<[a-z][\s\S]*>/i.test(rawValue);
    const baseHtml = hasHtml
      ? sanitizeProjectRichText(rawValue)
      : escapeHtml(rawValue).replace(/\n/g, "<br>");
    const mentionLabels = mentions
      .map((mention) => mention?.name)
      .filter(Boolean)
      .map((name) => `@${name}`);

    if (!baseHtml || !mentionLabels.length || typeof window === "undefined" || !window.DOMParser || !window.NodeFilter) {
      return baseHtml;
    }

    const body = new window.DOMParser().parseFromString(baseHtml, "text/html").body;
    const pattern = new RegExp(`(${mentionLabels.map(escapeRegExp).join("|")})(?=\\s|\\.|,|;|:|!|\\?|$)`, "gi");
    const walker = document.createTreeWalker(body, window.NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let node = walker.nextNode();
    while (node) {
      textNodes.push(node);
      node = walker.nextNode();
    }

    textNodes.forEach((textNode) => {
      if (textNode.parentElement?.closest?.(`.${PROJECT_MENTION_CLASS}`)) {
        return;
      }
      const text = textNode.nodeValue || "";
      pattern.lastIndex = 0;
      if (!pattern.test(text)) {
        return;
      }
      pattern.lastIndex = 0;
      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      text.replace(pattern, (match, _label, offset) => {
        if (offset > lastIndex) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
        }
        const pill = document.createElement("span");
        pill.className = PROJECT_MENTION_CLASS;
        pill.textContent = match;
        fragment.appendChild(pill);
        lastIndex = offset + match.length;
        return match;
      });
      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      textNode.parentNode?.replaceChild(fragment, textNode);
    });

    return body.innerHTML;
  }, [mentions, rawValue]);

  if (!rawValue && !attachments.length) {
    return null;
  }

  return (
    <>
      {highlightedHtml && (
        <div
          className="new-hrm-project-rich-text new-hrm-project-comment-body"
          dangerouslySetInnerHTML={{ __html: highlightedHtml }}
        />
      )}
      {attachments.length > 0 && (
        <div className="new-hrm-project-comment-attachments">
          {attachments.map((file) => {
            const label = file.name || file.fileName || file.title || "Image";
            const key = file.id || file.fileId || label;
            return file.downloadUrl ? (
              <a key={key} href={file.downloadUrl} target="_blank" rel="noreferrer">
                <FiImage aria-hidden="true" />
                {label}
              </a>
            ) : (
              <span key={key}>
                <FiImage aria-hidden="true" />
                {label}
              </span>
            );
          })}
        </div>
      )}
    </>
  );
};

const isActiveProject = (project = {}) => {
  if (project.isActive !== undefined) {
    const normalizedActive = normalizeText(project.isActive);
    return !["false", "0", "no", "inactive"].includes(normalizedActive);
  }
  const status = normalizeText(project.status || "Active");
  return !["inactive", "closed", "completed", "cancelled", "canceled", "archived"].some((item) => status.includes(item));
};

const isEntityOnActiveProject = (entity = {}) => {
  if (entity.projectIsActive !== undefined) {
    const normalizedActive = normalizeText(entity.projectIsActive);
    return !["false", "0", "no", "inactive"].includes(normalizedActive);
  }
  const status = normalizeText(entity.projectStatus || "Active");
  return !["inactive", "closed", "completed", "cancelled", "canceled", "archived"].some((item) => status.includes(item));
};

const projectIdentityValues = (project = {}) =>
  [project.id, project.name, project.title, project.code, project.projectId].filter(Boolean).map(String);

const entityMatchesProject = (entity, project = {}) => {
  const projectValues = projectIdentityValues(project);
  const entityValues = [entity.projectId, entity.projectName, entity.projectCode].filter(Boolean).map(String);
  return projectValues.some((projectValue) => entityValues.includes(projectValue));
};

const toFiniteNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const firstProjectValue = (source = {}, keys = [], fallback = "") => {
  for (const key of keys) {
    const value = key.split(".").reduce((current, part) => current?.[part], source);
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return fallback;
};

const asProjectArray = (value) => (Array.isArray(value) ? value : []);

const normalizeProjectDetailResponse = (response, fallbackProject = {}) => {
  const envelope = response?.data ?? response ?? {};
  const data = envelope?.data && typeof envelope.data === "object" ? envelope.data : envelope;
  const project = data.project || data.record || fallbackProject || {};
  const summary = data.summary || project.summary || {};
  return {
    project: { ...fallbackProject, ...project },
    entities: asProjectArray(data.entities || project.entities),
    team: asProjectArray(data.team || data.people || data.allocations || project.team),
    summary,
    entityStatus:
      data.entityStatus ||
      data.statusBreakdown ||
      summary.entityStatus ||
      summary.statusBreakdown ||
      project.entityStatus ||
      project.statusBreakdown ||
      {},
    hoursByMonth: asProjectArray(data.hoursByMonth || data.monthlyHours || summary.hoursByMonth),
    sentiments: asProjectArray(data.sentiments || project.sentiments),
    permissions: data.permissions || project.permissions || {},
  };
};

const formatProjectHours = (value) => {
  const hours = toFiniteNumber(value);
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
};

const formatProjectDateTime = (value) => {
  if (!value) {
    return "Recently";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const sentimentTone = (value) => {
  const tone = normalizeText(value);
  if (tone.includes("red")) return "Red";
  if (tone.includes("amber") || tone.includes("yellow")) return "Amber";
  return "Green";
};

const getProjectStatusRows = (value = {}) => {
  if (Array.isArray(value)) {
    return value.map((item) => ({
      label: item.label || item.status || item.name || "Other",
      value: toFiniteNumber(item.value ?? item.count ?? item.total),
    }));
  }
  return Object.entries(value || {}).map(([label, count]) => ({ label, value: toFiniteNumber(count) }));
};

const projectMatchesEntity = (entity, projectId, project) =>
  !projectId || entityMatchesProject(entity, project || { id: projectId, name: projectId });

const assigneeMatchesEntity = (entity, assigneeId) =>
  !assigneeId ||
  entity.assignedToId === assigneeId ||
  entity.assignedToContactId === assigneeId ||
  entity.assignedToEmail === assigneeId ||
  entity.assignedTo === assigneeId;

const isUnassignedValue = (value) => ["", "unassigned"].includes(normalizeText(value));

const getPersonRecordId = (person = {}) => person.contactId || person.employeeSalesforceId || person.id || "";

const getAssigneeFilterValue = (person = {}) => person.contactId || person.employeeSalesforceId || person.email || person.id || person.name || "";

const getPersonAliases = (person = {}) =>
  [person.contactId, person.employeeSalesforceId, person.id, person.email, person.name].filter(Boolean).map(String);

const projectManagedByPerson = (project = {}, person = {}) => {
  const personAliases = getPersonAliases(person).map(normalizeText).filter(Boolean);
  if (!personAliases.length) {
    return false;
  }

  const projectManagerValues = [
    project.projectManagerId,
    project.projectManagerContactId,
    project.managerId,
    project.Project_Manager__c,
    project.Manager__c,
    project.projectManagerEmail,
    project.managerEmail,
    project.projectManagerName,
    project.managerName,
  ].map(normalizeText).filter(Boolean);

  return projectManagerValues.some((value) => personAliases.includes(value));
};

const personMatchesValue = (person = {}, value) => Boolean(value) && getPersonAliases(person).includes(String(value));

const entityOwnedByPerson = (entity = {}, person = {}) =>
  getPersonAliases(person).some((alias) => assigneeMatchesEntity(entity, alias));

const toFilterValues = (value) =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean);

const filterKeyFromValues = (values = []) => toFilterValues(values).slice().sort().join("|");

const projectMatchesAnyEntity = (entity, projectIds = [], projects = []) => {
  const selectedProjectIds = toFilterValues(projectIds);
  if (!selectedProjectIds.length) {
    return true;
  }

  return selectedProjectIds.some((projectId) =>
    projectMatchesEntity(entity, projectId, projects.find((project) => String(project.id) === String(projectId)))
  );
};

const assigneeMatchesAnyEntity = (entity, assignees = []) => {
  const selectedAssignees = assignees.filter((person) => person.value || person.id || person.name || person.email);
  if (!selectedAssignees.length) {
    return true;
  }

  return selectedAssignees.some((person) =>
    assigneeMatchesEntity(entity, person.value) ||
    assigneeMatchesEntity(entity, person.id) ||
    assigneeMatchesEntity(entity, person.name) ||
    assigneeMatchesEntity(entity, person.email)
  );
};

const monthMatchesAnyEntity = (entity, months = []) => {
  const selectedMonths = toFilterValues(months);
  return !selectedMonths.length || selectedMonths.includes(getEntityMonthValue(entity));
};

const priorityMatchesAnyEntity = (entity, priorities = []) => {
  const selectedPriorities = toFilterValues(priorities).map(normalizeText);
  return !selectedPriorities.length || selectedPriorities.includes(normalizeText(entity.priority || "Medium"));
};

const getMentionKey = (person = {}) =>
  String(person.id || person.contactId || person.employeeSalesforceId || person.email || person.name || "").trim();

const mergeMention = (currentMentions = [], person = {}) => {
  const mentionKey = getMentionKey(person);
  if (!mentionKey || currentMentions.some((mention) => getMentionKey(mention) === mentionKey)) {
    return currentMentions;
  }
  return [...currentMentions, person];
};

const personMatchesMentionQuery = (person = {}, query = "") => {
  const normalizedQuery = normalizeText(query);
  if (!normalizedQuery) {
    return true;
  }
  const tokens = normalizeText(`${person.name} ${person.email}`)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  return tokens.some((token) => token.startsWith(normalizedQuery));
};

const getTextPointAtOffset = (root, offset) => {
  if (!root || typeof window === "undefined" || typeof document === "undefined" || !document.createTreeWalker || !window.NodeFilter) {
    return null;
  }

  const targetOffset = Math.max(0, Number(offset) || 0);
  const walker = document.createTreeWalker(root, window.NodeFilter.SHOW_TEXT);
  let currentOffset = 0;
  let node = walker.nextNode();

  while (node) {
    const nodeLength = (node.nodeValue || "").length;
    if (targetOffset <= currentOffset + nodeLength) {
      return { node, offset: Math.max(0, targetOffset - currentOffset) };
    }
    currentOffset += nodeLength;
    node = walker.nextNode();
  }

  return { node: root, offset: root.childNodes.length };
};

export const mentionsFromRichText = (value = "", assignees = []) => {
  const rawText = stripProjectRichText(value).toLowerCase();
  const mentionQueries = Array.from(rawText.matchAll(/@([a-z0-9._-]{2,60})/g)).map((match) => match[1]);
  return assignees.filter((person) => {
    return (
      (person.name && rawText.includes(`@${person.name}`.toLowerCase())) ||
      mentionQueries.some((query) => personMatchesMentionQuery(person, query))
    );
  });
};

const getMentionMatchFromText = (text = "") => {
  const match = String(text || "").match(/(^|\s)@([A-Za-z0-9._ -]{0,60})$/);
  if (!match) {
    return null;
  }
  const query = match[2].trimStart();
  return {
    query,
    startOffset: text.length - match[2].length - 1,
    endOffset: text.length,
  };
};

export const projectEditorTestUtils = {
  createProjectTableHtml,
  decodeProjectRichText,
  getMentionMatchFromText,
  mentionClass: PROJECT_MENTION_CLASS,
  projectManagedByPerson,
  projectEditorHtml,
  mentionsFromRichText,
  sanitizeProjectRichText,
  stripProjectRichText,
};

const EmojiPickerButton = ({ disabled = false, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="new-hrm-emoji-picker">
      <button type="button" onClick={() => setIsOpen((open) => !open)} disabled={disabled} title="Emoji">
        <FiSmile aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="new-hrm-emoji-picker__menu" role="menu" aria-label="Emoji choices">
          {PROJECT_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                onSelect(emoji);
                setIsOpen(false);
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const RichTextEditor = ({
  ariaLabel = "Rich text editor",
  assignees = [],
  disabled = false,
  mentions = [],
  minHeight = 120,
  onChange,
  onMentionsChange = () => {},
  onPaste,
  placeholder = "Write details",
  value = "",
}) => {
  const editorRef = useRef(null);
  const isFocusedRef = useRef(false);
  const savedRangeRef = useRef(null);
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const [isLinkPanelOpen, setIsLinkPanelOpen] = useState(false);
  const [isTablePanelOpen, setIsTablePanelOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState("");
  const [linkError, setLinkError] = useState("");
  const [mentionQuery, setMentionQuery] = useState(null);
  const [tableColumns, setTableColumns] = useState(3);
  const [tableError, setTableError] = useState("");
  const [tableRows, setTableRows] = useState(3);
  const safeEditorHtml = useMemo(() => projectEditorHtml(value), [value]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || (isFocusedRef.current && safeEditorHtml !== "") || editor.innerHTML === safeEditorHtml) {
      return;
    }
    editor.innerHTML = safeEditorHtml;
  }, [safeEditorHtml]);

  const availableMentions = useMemo(() => {
    const query = normalizeText(mentionQuery || "");
    return assignees
      .filter((person) => getMentionKey(person))
      .filter((person) => personMatchesMentionQuery(person, query))
      .slice(0, 6);
  }, [assignees, mentionQuery]);

  useEffect(() => {
    setActiveMentionIndex(0);
  }, [mentionQuery, availableMentions.length]);

  const emitChange = () => {
    onChange(editorRef.current?.innerHTML || "");
  };

  const saveSelection = () => {
    const editor = editorRef.current;
    const selection = window.getSelection?.();
    if (!editor || !selection?.rangeCount || !editor.contains(selection.anchorNode)) {
      return;
    }
    savedRangeRef.current = selection.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    const editor = editorRef.current;
    const selection = window.getSelection?.();
    const range = savedRangeRef.current;
    if (!editor || !selection || !range) {
      return;
    }
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const getCurrentRange = () => {
    const editor = editorRef.current;
    const selection = window.getSelection?.();
    if (!editor || !selection?.rangeCount || !editor.contains(selection.anchorNode)) {
      return null;
    }
    return selection.getRangeAt(0);
  };

  const finishEditorMutation = () => {
    saveSelection();
    emitChange();
    detectMention();
  };

  const replaceSelectionWithNode = (node) => {
    focusEditor();
    restoreSelection();
    const selection = window.getSelection?.();
    const range = getCurrentRange();
    if (!selection || !range) {
      return false;
    }
    range.deleteContents();
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    finishEditorMutation();
    return true;
  };

  const listSelectedText = (listTagName) => {
    focusEditor();
    restoreSelection();
    const range = getCurrentRange();
    const text = range?.toString() || "";
    const lines = text.split(/\n+/).map((line) => line.trim()).filter(Boolean);
    if (!range || range.collapsed || !lines.length) {
      return false;
    }
    const list = document.createElement(listTagName);
    lines.forEach((line) => {
      const item = document.createElement("li");
      item.appendChild(document.createTextNode(line));
      list.appendChild(item);
    });
    return replaceSelectionWithNode(list);
  };

  const runCommand = (command, valueArg = null) => {
    if (disabled) {
      return;
    }
    focusEditor();
    restoreSelection();
    if (command === "bold" || command === "italic") {
      document.execCommand("styleWithCSS", false, "false");
      document.execCommand(command, false, valueArg);
      finishEditorMutation();
      return;
    }
    if (command === "insertUnorderedList" && listSelectedText("ul")) {
      return;
    }
    if (command === "insertOrderedList" && listSelectedText("ol")) {
      return;
    }
    if (command === "insertText" && replaceSelectionWithNode(document.createTextNode(valueArg || ""))) {
      return;
    }
    document.execCommand(command, false, valueArg);
    finishEditorMutation();
  };

  const getTextBeforeCaret = () => {
    const editor = editorRef.current;
    const selection = window.getSelection?.();
    if (!editor || !selection?.rangeCount || !editor.contains(selection.anchorNode)) {
      return "";
    }
    const range = selection.getRangeAt(0).cloneRange();
    const beforeRange = range.cloneRange();
    beforeRange.selectNodeContents(editor);
    beforeRange.setEnd(range.endContainer, range.endOffset);
    return beforeRange.toString();
  };

  const getCurrentMentionMatch = () => {
    const beforeCaret = getTextBeforeCaret() || editorRef.current?.innerText || "";
    return getMentionMatchFromText(beforeCaret);
  };

  const detectMention = () => {
    setMentionQuery(getCurrentMentionMatch()?.query ?? null);
  };

  const deleteTypedMention = () => {
    const selection = window.getSelection?.();
    const editor = editorRef.current;
    const match = getCurrentMentionMatch();
    if (!selection?.rangeCount || !editor || !match) {
      return null;
    }
    const range = selection.getRangeAt(0);
    const startPoint = getTextPointAtOffset(editor, match.startOffset);
    if (!startPoint) {
      return null;
    }
    const replacementRange = document.createRange();
    replacementRange.setStart(startPoint.node, startPoint.offset);
    replacementRange.setEnd(range.endContainer, range.endOffset);
    replacementRange.deleteContents();
    return replacementRange;
  };

  const insertMention = (person) => {
    if (disabled) {
      return;
    }
    focusEditor();
    restoreSelection();
    const selection = window.getSelection?.();
    if (!selection?.rangeCount) {
      return;
    }
    const range = deleteTypedMention() || selection.getRangeAt(0);
    const mentionNode = document.createElement("span");
    mentionNode.className = PROJECT_MENTION_CLASS;
    mentionNode.contentEditable = "false";
    mentionNode.textContent = `@${person.name}`;
    const trailingSpace = document.createTextNode(" ");
    range.insertNode(trailingSpace);
    range.insertNode(mentionNode);
    range.setStartAfter(trailingSpace);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    saveSelection();
    onMentionsChange(mergeMention(mentions, person));
    setMentionQuery(null);
    setActiveMentionIndex(0);
    emitChange();
  };

  const insertEmoji = (emoji) => {
    runCommand("insertText", emoji);
  };

  const openMentionPicker = () => {
    if (disabled) {
      return;
    }
    focusEditor();
    restoreSelection();
    const editor = editorRef.current;
    const selection = window.getSelection?.();
    if (!editor || !selection) {
      return;
    }
    let range = getCurrentRange();
    if (!range) {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    range.deleteContents();
    const trigger = document.createTextNode("@");
    range.insertNode(trigger);
    range.setStartAfter(trigger);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    saveSelection();
    emitChange();
    setMentionQuery("");
  };

  const applyTextColor = (color) => {
    const safeColor = sanitizeRichTextColor(color);
    if (disabled || !safeColor) {
      return;
    }
    focusEditor();
    restoreSelection();
    const selection = window.getSelection?.();
    const range = getCurrentRange();
    if (!selection || !range || range.collapsed) {
      document.execCommand("foreColor", false, safeColor);
      finishEditorMutation();
      return;
    }
    const colorNode = document.createElement("span");
    colorNode.style.color = safeColor;
    colorNode.appendChild(range.extractContents());
    range.insertNode(colorNode);
    range.setStartAfter(colorNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    finishEditorMutation();
  };

  const getSelectedTableCell = () => {
    focusEditor();
    restoreSelection();
    const editor = editorRef.current;
    const range = getCurrentRange();
    if (!editor || !range) {
      return null;
    }
    const startNode = range.startContainer?.nodeType === 3 ? range.startContainer.parentElement : range.startContainer;
    const cell = startNode?.closest?.("td, th");
    return cell && editor.contains(cell) ? cell : null;
  };

  const insertTable = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (disabled) {
      return;
    }
    replaceSelectionWithNode(createProjectTableElement(tableRows, tableColumns));
    setIsTablePanelOpen(false);
    setTableError("");
  };

  const addTableRow = () => {
    if (disabled) {
      return;
    }
    const cell = getSelectedTableCell();
    if (!cell) {
      setTableError("Place the cursor inside a table.");
      return;
    }
    const row = cell.closest("tr");
    const table = cell.closest("table");
    const columnCount = table?.rows?.[0]?.cells?.length || row?.cells?.length || 1;
    const nextRow = document.createElement("tr");
    Array.from({ length: columnCount }).forEach(() => {
      const nextCell = document.createElement("td");
      nextCell.appendChild(document.createElement("br"));
      nextRow.appendChild(nextCell);
    });
    row.parentNode.insertBefore(nextRow, row.nextSibling);
    setTableError("");
    finishEditorMutation();
  };

  const addTableColumn = () => {
    if (disabled) {
      return;
    }
    const cell = getSelectedTableCell();
    if (!cell) {
      setTableError("Place the cursor inside a table.");
      return;
    }
    const table = cell.closest("table");
    const columnIndex = Array.from(cell.parentNode.children).indexOf(cell);
    Array.from(table.rows).forEach((row, rowIndex) => {
      const isHeader = row.parentElement?.tagName?.toLowerCase() === "thead" || rowIndex === 0;
      const nextCell = document.createElement(isHeader ? "th" : "td");
      nextCell.appendChild(isHeader ? document.createTextNode(`Column ${columnIndex + 2}`) : document.createElement("br"));
      row.insertBefore(nextCell, row.children[columnIndex + 1] || null);
    });
    setTableError("");
    finishEditorMutation();
  };

  const insertLink = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    const safeUrl = normalizeRichTextUrl(linkDraft);
    if (!safeUrl) {
      setLinkError("Use a valid http, https, mailto, or tel link.");
      return;
    }
    focusEditor();
    restoreSelection();
    const selection = window.getSelection?.();
    const range = getCurrentRange();
    if (!selection || !range) {
      setLinkError("Place the cursor where the link should be added.");
      return;
    }
    const anchor = document.createElement("a");
    anchor.href = safeUrl;
    anchor.target = "_blank";
    anchor.rel = "noreferrer";
    if (range.collapsed) {
      anchor.textContent = safeUrl;
    } else {
      anchor.appendChild(range.extractContents());
    }
    range.insertNode(anchor);
    range.setStartAfter(anchor);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    setIsLinkPanelOpen(false);
    setLinkDraft("");
    setLinkError("");
    finishEditorMutation();
  };

  const hasMentionMenu = mentionQuery !== null && availableMentions.length > 0;

  return (
    <div className="new-hrm-rich-field">
      <div className="new-hrm-rich-field__toolbar" aria-label="Text formatting">
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("bold")} disabled={disabled} title="Bold">
          <FiBold aria-hidden="true" />
        </button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("italic")} disabled={disabled} title="Italic">
          <FiItalic aria-hidden="true" />
        </button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("insertUnorderedList")} disabled={disabled} title="Bullet list">
          <FiList aria-hidden="true" />
        </button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("insertOrderedList")} disabled={disabled} title="Numbered list">
          <FiHash aria-hidden="true" />
        </button>
        <div className="new-hrm-rich-field__table-menu">
          <button
            type="button"
            onMouseDown={(event) => {
              event.preventDefault();
              saveSelection();
            }}
            onClick={() => {
              saveSelection();
              setTableError("");
              setIsTablePanelOpen((open) => !open);
            }}
            disabled={disabled}
            title="Table"
            aria-expanded={isTablePanelOpen}
          >
            <FiGrid aria-hidden="true" />
          </button>
          {isTablePanelOpen && (
            <div className="new-hrm-rich-field__table-popover" role="group" aria-label="Insert table" onClick={(event) => event.stopPropagation()}>
              <div className="new-hrm-rich-field__table-size">
                <label>
                  <span>Rows</span>
                  <input
                    min={PROJECT_TABLE_MIN_SIZE}
                    max={PROJECT_TABLE_MAX_SIZE}
                    type="number"
                    value={tableRows}
                    onChange={(event) => {
                      setTableRows(clampProjectTableSize(event.target.value, 3));
                      setTableError("");
                    }}
                  />
                </label>
                <label>
                  <span>Columns</span>
                  <input
                    min={PROJECT_TABLE_MIN_SIZE}
                    max={PROJECT_TABLE_MAX_SIZE}
                    type="number"
                    value={tableColumns}
                    onChange={(event) => {
                      setTableColumns(clampProjectTableSize(event.target.value, 3));
                      setTableError("");
                    }}
                  />
                </label>
              </div>
              <div className="new-hrm-rich-field__table-actions">
                <button
                  type="button"
                  className="new-hrm-rich-field__popover-primary"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={insertTable}
                >
                  Insert table
                </button>
                <button type="button" onMouseDown={(event) => event.stopPropagation()} onClick={addTableRow}>
                  <FiPlus aria-hidden="true" />
                  Row
                </button>
                <button type="button" onMouseDown={(event) => event.stopPropagation()} onClick={addTableColumn}>
                  <FiColumns aria-hidden="true" />
                  Column
                </button>
              </div>
              {tableError && <small>{tableError}</small>}
            </div>
          )}
        </div>
        <div className="new-hrm-rich-field__color-menu" aria-label="Text color">
          <span title="Text color">
            <FiDroplet aria-hidden="true" />
          </span>
          {PROJECT_TEXT_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              className="new-hrm-rich-field__color-swatch"
              style={{ "--swatch-color": color }}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => applyTextColor(color)}
              disabled={disabled}
              title={`Text color ${color}`}
            />
          ))}
        </div>
        <div className="new-hrm-rich-field__link-menu">
          <button
            type="button"
            onMouseDown={(event) => {
              event.preventDefault();
              saveSelection();
            }}
            onClick={() => {
              saveSelection();
              setLinkError("");
              setIsLinkPanelOpen((open) => !open);
            }}
            disabled={disabled}
            title="Add link"
            aria-expanded={isLinkPanelOpen}
          >
            <FiLink aria-hidden="true" />
          </button>
          {isLinkPanelOpen && (
            <div className="new-hrm-rich-field__link-popover" role="group" aria-label="Apply link" onClick={(event) => event.stopPropagation()}>
              <label>
                <span>Link URL</span>
                <input
                  autoFocus
                  value={linkDraft}
                  onChange={(event) => {
                    setLinkDraft(event.target.value);
                    setLinkError("");
                  }}
                  placeholder="https://example.com"
                />
              </label>
              {linkError && <small>{linkError}</small>}
              <div>
                <button type="button" onMouseDown={(event) => event.stopPropagation()} onClick={() => setIsLinkPanelOpen(false)}>Cancel</button>
                <button
                  type="button"
                  className="new-hrm-rich-field__popover-primary"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={insertLink}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
        {assignees.length > 0 && (
          <button
            type="button"
            aria-label="Tag a person"
            disabled={disabled}
            onMouseDown={(event) => {
              event.preventDefault();
              saveSelection();
            }}
            onClick={openMentionPicker}
            title="Tag a person"
          >
            <FiAtSign aria-hidden="true" />
          </button>
        )}
        <EmojiPickerButton disabled={disabled} onSelect={insertEmoji} />
      </div>
      <div className={`new-hrm-rich-field__editor-wrap ${hasMentionMenu ? "new-hrm-rich-field__editor-wrap--mentioning" : ""}`}>
        <div
          ref={editorRef}
          className="new-hrm-rich-field__editor"
          contentEditable={!disabled}
          data-placeholder={placeholder}
          aria-label={ariaLabel}
          onBlur={() => {
            isFocusedRef.current = false;
            setMentionQuery(null);
          }}
          onFocus={() => {
            isFocusedRef.current = true;
            saveSelection();
          }}
          onInput={() => {
            emitChange();
            saveSelection();
            detectMention();
          }}
          onKeyDown={(event) => {
            if (hasMentionMenu) {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setActiveMentionIndex((index) => (index + 1) % availableMentions.length);
                return;
              }
              if (event.key === "ArrowUp") {
                event.preventDefault();
                setActiveMentionIndex((index) => (index - 1 + availableMentions.length) % availableMentions.length);
                return;
              }
              if (event.key === "Enter" || event.key === "Tab") {
                event.preventDefault();
                insertMention(availableMentions[activeMentionIndex] || availableMentions[0]);
                return;
              }
              if (event.key === "Escape") {
                event.preventDefault();
                setMentionQuery(null);
                return;
              }
            }
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "b") {
              event.preventDefault();
              runCommand("bold");
            }
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "i") {
              event.preventDefault();
              runCommand("italic");
            }
            if (event.key === "Escape") {
              setMentionQuery(null);
            }
          }}
          onKeyUp={() => {
            saveSelection();
            detectMention();
          }}
          onMouseUp={() => {
            saveSelection();
            detectMention();
          }}
          onPaste={onPaste}
          role="textbox"
          aria-multiline="true"
          spellCheck={false}
          style={{ minHeight }}
          suppressContentEditableWarning
        />
        {hasMentionMenu && (
          <div className="new-hrm-project-mention-menu" role="listbox" aria-label="Tag an employee">
            {availableMentions.map((person, index) => (
              <button
                key={getMentionKey(person)}
                type="button"
                className={index === activeMentionIndex ? "new-hrm-project-mention-menu__item--active" : ""}
                role="option"
                aria-selected={index === activeMentionIndex}
                onMouseEnter={() => setActiveMentionIndex(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  insertMention(person);
                }}
              >
                <span className="new-hrm-avatar new-hrm-avatar--small">{getInitials(person.name)}</span>
                <strong>{person.name}</strong>
                <small>{person.email}</small>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const RichDescriptionField = ({
  assignees = [],
  disabled = false,
  mentions = [],
  minHeight = 140,
  onChange,
  onMentionsChange = () => {},
  placeholder = "Write details",
  value,
}) => (
  <RichTextEditor
    assignees={assignees}
    disabled={disabled}
    mentions={mentions}
    minHeight={minHeight}
    onChange={onChange}
    onMentionsChange={onMentionsChange}
    placeholder={placeholder}
    value={value}
  />
);

const MultiSelectFilter = ({ allLabel, label, onChange, options = [], searchable = false, selectedValues = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const rootRef = useRef(null);
  const selectedSet = useMemo(() => new Set(toFilterValues(selectedValues)), [selectedValues]);
  const normalizedValues = useMemo(() => toFilterValues(selectedValues), [selectedValues]);
  const normalizedFilterText = normalizeText(filterText);
  const visibleOptions = useMemo(
    () =>
      normalizedFilterText
        ? options.filter((option) => normalizeText(`${option.label} ${option.value}`).includes(normalizedFilterText))
        : options,
    [normalizedFilterText, options]
  );
  const selectedLabels = normalizedValues
    .map((value) => options.find((option) => String(option.value) === value)?.label)
    .filter(Boolean);
  const hasSelectedEveryOption =
    options.length > 0 &&
    normalizedValues.length >= options.length &&
    options.every((option) => selectedSet.has(String(option.value)));
  const displayValue =
    hasSelectedEveryOption
      ? allLabel
      : selectedLabels.length === 0
      ? normalizedValues.length
        ? `${normalizedValues.length} selected`
        : allLabel
      : selectedLabels.length === 1
        ? selectedLabels[0]
        : `${selectedLabels.length} selected`;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen]);

  const toggleOption = (value) => {
    const nextValue = String(value);
    const nextValues = selectedSet.has(nextValue)
      ? normalizedValues.filter((item) => item !== nextValue)
      : [...normalizedValues, nextValue];
    onChange(nextValues);
  };

  return (
    <div className="new-hrm-project-multi-filter" ref={rootRef}>
      <button
        type="button"
        className="new-hrm-project-multi-filter__button"
        onClick={() => setIsOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{label}</span>
        <strong>{displayValue}</strong>
        <FiChevronDown aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="new-hrm-project-multi-filter__menu" role="listbox" aria-label={label}>
          {searchable && (
            <label className="new-hrm-project-multi-filter__search">
              <FiSearch aria-hidden="true" />
              <input
                autoFocus
                type="search"
                value={filterText}
                onChange={(event) => setFilterText(event.target.value)}
                placeholder={`Search ${label.toLowerCase()}`}
              />
            </label>
          )}
          <button
            type="button"
            className="new-hrm-project-multi-filter__clear"
            onClick={() => onChange([])}
          >
            {allLabel}
          </button>
          {visibleOptions.map((option) => (
            <label key={option.value}>
              <input
                type="checkbox"
                checked={selectedSet.has(String(option.value))}
                onChange={() => toggleOption(option.value)}
              />
              {option.color && (
                <i
                  className="new-hrm-project-filter-color"
                  style={{ "--project-color": normalizeProjectColor(option.color) }}
                  aria-hidden="true"
                />
              )}
              <span>{option.label}</span>
            </label>
          ))}
          {!visibleOptions.length && (
            <p className="new-hrm-project-multi-filter__empty">No matches found</p>
          )}
        </div>
      )}
    </div>
  );
};

const ProjectEntityCard = ({ entity, isUpdating, onClick, onDragStart, onExpand, onProjectOpen }) => {
  const openEntity = () => onClick(entity.id);
  const handleKeyDown = (event) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openEntity();
    }
  };

  return (
    <article
      className="new-hrm-project-entity-card"
      draggable
      role="button"
      tabIndex={0}
      style={{ "--project-color": normalizeProjectColor(entity.projectColor) }}
      onClick={openEntity}
      onDragStart={(event) => onDragStart(event, entity.id)}
      onKeyDown={handleKeyDown}
    >
      <span className="new-hrm-project-entity-card__top">
        <em className={`new-hrm-project-priority new-hrm-project-priority--${toTone(entity.priority)}`}>
          {entity.priority || "Medium"}
        </em>
        <span className="new-hrm-project-entity-card__actions">
          <small className={`new-hrm-project-status new-hrm-project-status--${toTone(entity.status)}`}>
            {isUpdating ? "Saving" : entity.status}
          </small>
          <button
            type="button"
            className="new-hrm-project-entity-card__expand"
            onClick={(event) => {
              event.stopPropagation();
              onExpand(entity.id);
            }}
            onMouseDown={(event) => event.stopPropagation()}
            aria-label={`Expand ${entity.title || entity.key || "entity"}`}
            title="Expand entity"
          >
            <FiMaximize2 aria-hidden="true" />
          </button>
        </span>
      </span>
      <strong>{entity.title}</strong>
      <button
        type="button"
        className="new-hrm-project-entity-card__project"
        onClick={(event) => {
          event.stopPropagation();
          onProjectOpen(entity.projectId);
        }}
        onPointerDown={(event) => event.stopPropagation()}
        aria-label={`Open ${entity.projectName || "project"} project`}
      >
        <i aria-hidden="true" />
        {entity.projectName}
      </button>
      <span className="new-hrm-project-entity-card__key">{entity.key}</span>
      <p>{stripProjectRichText(entity.description) || "No description provided."}</p>
      <span className="new-hrm-project-entity-card__footer">
        <span className="new-hrm-avatar new-hrm-avatar--small">{getInitials(entity.assignedTo)}</span>
        <span>{entity.assignedTo || "Unassigned"}</span>
        {entity.estimatedHours ? <time>{entity.estimatedHours}h est.</time> : entity.dueDate && <time>{entity.dueDate}</time>}
      </span>
    </article>
  );
};

const ProjectModal = ({ assignees, isSaving, onAccountSearch = async () => [], onClose, onCreate }) => {
  const [form, setForm] = useState({
    title: "",
    description: "",
    accountId: "",
    accountName: "",
    accountSearch: "",
    projectManagerId: getPersonRecordId(assignees[0]) || "",
    color: "#2454E8",
  });
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState("");
  const isColorValid = isValidProjectHexColor(form.color);
  const accountSearchText = form.accountSearch.trim();
  const shouldShowAccountResults = !form.accountId && accountSearchText.length >= 2;

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateColor = (value) => updateField("color", String(value || "").trim().toUpperCase());
  const updateAccountSearch = (value) =>
    setForm((current) => ({
      ...current,
      accountId: "",
      accountName: "",
      accountSearch: value,
    }));
  const selectAccount = (account = {}) =>
    setForm((current) => ({
      ...current,
      accountId: account.accountId || account.id || "",
      accountName: account.name || account.label || "",
      accountSearch: account.name || account.label || "",
    }));

  useEffect(() => {
    let isActive = true;
    const searchText = form.accountSearch.trim();
    if (form.accountId || searchText.length < 2) {
      setAccountOptions([]);
      setAccountLoading(false);
      setAccountError("");
      return () => {
        isActive = false;
      };
    }

    setAccountLoading(true);
    setAccountError("");
    const timer = window.setTimeout(async () => {
      try {
        const accounts = await onAccountSearch(searchText);
        if (isActive) {
          setAccountOptions(accounts);
          setAccountLoading(false);
        }
      } catch (error) {
        if (isActive) {
          setAccountOptions([]);
          setAccountError(error.message || "Accounts could not be loaded.");
          setAccountLoading(false);
        }
      }
    }, 250);

    return () => {
      isActive = false;
      window.clearTimeout(timer);
    };
  }, [form.accountId, form.accountSearch, onAccountSearch]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSaving) {
      return;
    }
    if (!isColorValid || !form.accountId) {
      return;
    }
    const didCreate = await onCreate(form);
    if (didCreate !== false) {
      onClose();
    }
  };

  return (
    <ProjectOverlay loading={isSaving} loadingLabel="Creating project" title="New Project" onClose={onClose}>
      <form className="new-hrm-project-form" onSubmit={handleSubmit} aria-busy={isSaving}>
        <label>
          <span>Project Title</span>
          <input value={form.title} onChange={(event) => updateField("title", event.target.value)} required />
        </label>
        <label>
          <span>Project Manager</span>
          <select value={form.projectManagerId} onChange={(event) => updateField("projectManagerId", event.target.value)} required>
            {assignees.map((person) => (
              <option key={person.id || person.email || person.name} value={getPersonRecordId(person)}>
                {person.name}
              </option>
            ))}
          </select>
        </label>
        <label className="new-hrm-project-form__full new-hrm-project-account-field">
          <span>Account</span>
          <input
            type="search"
            value={form.accountSearch}
            onChange={(event) => updateAccountSearch(event.target.value)}
            placeholder="Search account"
            required
          />
          <input type="hidden" value={form.accountId} required readOnly />
          {shouldShowAccountResults && (
            <div className="new-hrm-project-account-results">
              {accountLoading ? (
                <span>Loading accounts...</span>
              ) : accountError ? (
                <span className="new-hrm-project-account-results__error">{accountError}</span>
              ) : accountOptions.length === 0 ? (
                <span>No matching accounts found.</span>
              ) : (
                accountOptions.map((account) => (
                  <button key={account.accountId || account.id} type="button" onClick={() => selectAccount(account)}>
                    <strong>{account.name || account.label}</strong>
                    <small>{account.accountId || account.id}</small>
                  </button>
                ))
              )}
            </div>
          )}
          {form.accountSearch && !form.accountId && !accountLoading && (
            <small className="new-hrm-project-field-error">
              {accountSearchText.length < 2 ? "Type at least 2 characters to search accounts." : "Select an account from the list."}
            </small>
          )}
        </label>
        <label>
          <span>Project Color</span>
          <div className="new-hrm-project-color-field">
            <input
              aria-label="Project color picker"
              type="color"
              value={isColorValid ? form.color : "#2454E8"}
              onChange={(event) => updateColor(event.target.value)}
            />
            <input
              aria-invalid={!isColorValid}
              pattern="#[0-9A-Fa-f]{6}"
              placeholder="#2563EB"
              value={form.color}
              onChange={(event) => updateColor(event.target.value)}
              required
            />
          </div>
          {!isColorValid && <small className="new-hrm-project-field-error">Use a hex code like #2563EB.</small>}
        </label>
        <label className="new-hrm-project-form__full">
          <span>Description</span>
          <textarea rows={4} value={form.description} onChange={(event) => updateField("description", event.target.value)} />
        </label>
        <div className="new-hrm-project-form__actions">
          <button type="button" className="new-hrm-secondary-button" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button type="submit" className="new-hrm-primary-button" disabled={isSaving || !form.title.trim() || !form.accountId || !isColorValid}>
            {isSaving ? "Creating..." : "Create Project"}
          </button>
        </div>
      </form>
    </ProjectOverlay>
  );
};

const EntityModal = ({ assignees, initialProjectId, isSaving, onClose, onCreate, projects, statusOptions = STATUS_OPTIONS, recordTypes = [] }) => {
  const formId = useId();
  const firstProjectId = initialProjectId || projects[0]?.id || "";
  const [form, setForm] = useState({
    ...(recordTypes.length ? { recordType: recordTypes.includes("Task") ? "Task" : recordTypes[0] } : {}),
    projectId: firstProjectId,
    title: "",
    assignedToId: getPersonRecordId(assignees[0]) || "",
    priority: "Medium",
    status: "Defined",
    estimatedHours: "",
    dueDate: "",
    description: "",
    descriptionMentions: [],
    files: [],
  });

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateFiles = (fileList) => {
    const nextFiles = Array.from(fileList || []);
    setForm((current) => ({ ...current, files: [...current.files, ...nextFiles] }));
  };
  const handlePasteFiles = (event) => {
    const pastedImages = imageFilesFromClipboard(event, "project-entity-image");
    if (!pastedImages.length) {
      return;
    }
    event.preventDefault();
    updateFiles(pastedImages);
  };
  const removeFile = (fileIndex) => {
    setForm((current) => ({
      ...current,
      files: current.files.filter((_, index) => index !== fileIndex),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSaving) {
      return;
    }
    const assignee = assignees.find((person) => personMatchesValue(person, form.assignedToId));
    const descriptionMentions = mentionsFromRichText(form.description, assignees, form.descriptionMentions);
    const didCreate = await onCreate({
      ...form,
      assignedTo: assignee?.name || "",
      descriptionMentions,
      mentions: descriptionMentions,
    });
    if (didCreate !== false) {
      onClose();
    }
  };

  return (
    <ProjectOverlay loading={isSaving} loadingLabel="Creating project entity" size="large" title="New Project Entity" onClose={onClose}>
      <form className="new-hrm-project-form new-hrm-project-form--entity" onSubmit={handleSubmit} onPaste={handlePasteFiles} aria-busy={isSaving}>
        <label className="new-hrm-project-form__half" htmlFor={`${formId}-project`}>
          <span>Project</span>
          <select id={`${formId}-project`} aria-label="Project" value={form.projectId} onChange={(event) => updateField("projectId", event.target.value)} required>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label className="new-hrm-project-form__half" htmlFor={`${formId}-assignee`}>
          <span>Assign To</span>
          <select id={`${formId}-assignee`} aria-label="Assign To" value={form.assignedToId} onChange={(event) => updateField("assignedToId", event.target.value)} required>
            {assignees.map((person) => (
              <option key={person.id || person.email || person.name} value={getPersonRecordId(person)}>
                {person.name}
              </option>
            ))}
          </select>
        </label>
        <label className="new-hrm-project-form__full" htmlFor={`${formId}-title`}>
          <span>Title</span>
          <input id={`${formId}-title`} aria-label="Title" value={form.title} onChange={(event) => updateField("title", event.target.value)} required />
        </label>
        {recordTypes.length > 0 && (
          <label htmlFor={`${formId}-type`}>
            <span>Type</span>
            <select id={`${formId}-type`} aria-label="Type" value={form.recordType} onChange={(event) => updateField("recordType", event.target.value)}>
              {recordTypes.map((type) => <option key={type} value={type}>{type.replace(/_/g, " ")}</option>)}
            </select>
          </label>
        )}
        <label htmlFor={`${formId}-priority`}>
          <span>Priority</span>
          <select id={`${formId}-priority`} aria-label="Priority" value={form.priority} onChange={(event) => updateField("priority", event.target.value)}>
            {PRIORITY_OPTIONS.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
          </select>
        </label>
        <label htmlFor={`${formId}-status`}>
          <span>Status</span>
          <select id={`${formId}-status`} aria-label="Status" value={form.status} onChange={(event) => updateField("status", event.target.value)}>
            {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
        </label>
        <label htmlFor={`${formId}-estimate`}>
          <span>Estimated Hours</span>
          <input id={`${formId}-estimate`} aria-label="Estimated Hours" type="number" min="0" step="0.5" value={form.estimatedHours} onChange={(event) => updateField("estimatedHours", event.target.value)} />
        </label>
        <label htmlFor={`${formId}-due`}>
          <span>Due Date</span>
          <input id={`${formId}-due`} aria-label="Due Date" type="date" value={form.dueDate} onChange={(event) => updateField("dueDate", event.target.value)} />
        </label>
        <div className="new-hrm-project-form__full new-hrm-project-field-group">
          <span className="new-hrm-project-field-label">Description</span>
          <RichDescriptionField
            assignees={assignees}
            mentions={form.descriptionMentions}
            onMentionsChange={(mentions) => updateField("descriptionMentions", mentions)}
            value={form.description}
            onChange={(value) => updateField("description", value)}
            minHeight={260}
            placeholder="Write acceptance notes, links, and implementation context. Type @ to tag someone."
          />
        </div>
        <label className="new-hrm-project-form__full">
          <span>Files</span>
          <input type="file" multiple onChange={(event) => {
            updateFiles(event.target.files);
            event.target.value = "";
          }} />
          <small className="new-hrm-project-upload-hint">Paste screenshots anywhere in this form, or select files here.</small>
          {form.files.length > 0 && (
            <div className="new-hrm-project-file-list">
              {form.files.map((file, index) => (
                <span key={`${file.name}-${file.size}-${index}`}>
                  {file.type?.startsWith("image/") ? <FiImage aria-hidden="true" /> : <FiFileText aria-hidden="true" />}
                  <strong>{file.name}</strong>
                  <small>{formatProjectFileSize(file.size)}</small>
                  <button type="button" onClick={() => removeFile(index)} aria-label={`Remove ${file.name}`}>
                    <FiX aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </label>
        <div className="new-hrm-project-form__actions">
          <button type="button" className="new-hrm-secondary-button" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button type="submit" className="new-hrm-primary-button" disabled={isSaving || !form.title.trim() || !form.projectId}>
            {isSaving ? "Creating..." : "Create Entity"}
          </button>
        </div>
      </form>
    </ProjectOverlay>
  );
};

const ProjectOverlay = ({ children, className = "", loading = false, loadingLabel = "Saving", onClose, size = "default", title }) => {
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !loading) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [loading, onClose]);

  const handleBackdropPointerDown = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  return (
    <div className="new-hrm-project-dialog-backdrop" onPointerDown={handleBackdropPointerDown} role="presentation">
      <section
        className={`new-hrm-project-dialog ${size === "large" ? "new-hrm-project-dialog--large" : ""} ${className} new-hrm-loader-host`.trim()}
        aria-busy={loading}
        aria-label={title}
        aria-modal="true"
        role="dialog"
      >
        <header>
          <h2>{title}</h2>
          <button type="button" className="new-hrm-icon-button" onClick={handleClose} disabled={loading} aria-label={`Close ${title}`}>
            <FiX aria-hidden="true" />
          </button>
        </header>
        {children}
        <ComponentLoader active={loading} label={loadingLabel} />
      </section>
    </div>
  );
};

const ProjectWorkspaceModal = ({ assignees = [], fallbackProject = {}, onClose, projectId, viewUserId, projectDetailApi = getProjectDetail, projectSentimentApi = addProjectSentiment, enableSentiments = true }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState({ sentiment: "Green", stakeholder: "", description: "" });

  const loadProject = useCallback(async ({ quiet = false } = {}) => {
    if (!projectId) {
      setError("Project detail is not available.");
      setLoading(false);
      return;
    }
    if (!quiet) {
      setLoading(true);
    }
    setError("");
    try {
      const response = await projectDetailApi(viewUserId, projectId);
      setDetail(normalizeProjectDetailResponse(response, fallbackProject));
    } catch (requestError) {
      setError(requestError?.message || "Project detail could not be loaded.");
    } finally {
      if (!quiet) {
        setLoading(false);
      }
    }
  }, [fallbackProject, projectDetailApi, projectId, viewUserId]);

  useEffect(() => {
    setDetail(null);
    setForm({ sentiment: "Green", stakeholder: "", description: "" });
    setFormError("");
    loadProject();
  }, [loadProject]);

  const normalizedDetail = detail || normalizeProjectDetailResponse({}, fallbackProject);
  const project = normalizedDetail.project || fallbackProject;
  const entities = normalizedDetail.entities;
  const team = normalizedDetail.team;
  const summary = normalizedDetail.summary || {};
  const sentiments = useMemo(
    () => [...normalizedDetail.sentiments].sort((left, right) => {
      const leftTime = new Date(firstProjectValue(left, ["createdAt", "recordedAt", "date", "updatedAt"], 0)).getTime() || 0;
      const rightTime = new Date(firstProjectValue(right, ["createdAt", "recordedAt", "date", "updatedAt"], 0)).getTime() || 0;
      return rightTime - leftTime;
    }),
    [normalizedDetail.sentiments]
  );
  const hoursByMonth = normalizedDetail.hoursByMonth;
  const statusRows = useMemo(() => {
    const suppliedRows = getProjectStatusRows(normalizedDetail.entityStatus).filter((row) => row.value > 0);
    if (suppliedRows.length) {
      return suppliedRows;
    }
    const counts = entities.reduce((rows, entity) => {
      const label = entity.status || "Other";
      rows[label] = (rows[label] || 0) + 1;
      return rows;
    }, {});
    return getProjectStatusRows(counts);
  }, [entities, normalizedDetail.entityStatus]);
  const maxStatusCount = Math.max(...statusRows.map((row) => row.value), 1);
  const maxMonthlyHours = Math.max(
    ...hoursByMonth.map((row) => toFiniteNumber(firstProjectValue(row, ["hours", "loggedHours", "totalHours", "value"]))),
    1
  );
  const projectName = firstProjectValue(project, ["name", "title", "projectName"], "Project workspace");
  const projectCode = firstProjectValue(project, ["code", "projectCode", "key"]);
  const projectColor = normalizeProjectColor(firstProjectValue(project, ["color", "colour", "colorCode", "projectColor"]));
  const managerName = firstProjectValue(project, ["projectManagerName", "managerName", "projectManager.name"], "Not assigned");
  const accountName = firstProjectValue(project, ["accountName", "account.name", "clientName"], "Internal");
  const loggedHours = firstProjectValue(summary, ["loggedHours", "totalLoggedHours", "totalHours"], entities.reduce((total, entity) => total + toFiniteNumber(entity.actualHours || entity.loggedHours), 0));
  const approvedHours = firstProjectValue(summary, ["approvedHours", "totalApprovedHours"], team.reduce((total, member) => total + toFiniteNumber(member.approvedHours), 0));
  const canAddSentiment = enableSentiments && Boolean(normalizedDetail.permissions?.canAddSentiment);
  const descriptionIsEmpty = !stripProjectRichText(form.description);
  const canSaveSentiment = canAddSentiment && form.stakeholder.trim() && !descriptionIsEmpty && !saving;

  const submitSentiment = async (event) => {
    event.preventDefault();
    if (!canAddSentiment) return;
    if (!form.stakeholder.trim() || descriptionIsEmpty) {
      setFormError("Stakeholder and description are required.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await projectSentimentApi(viewUserId, projectId, {
        sentiment: form.sentiment,
        stakeholder: form.stakeholder.trim(),
        description: form.description,
      });
      setForm({ sentiment: "Green", stakeholder: "", description: "" });
      await loadProject({ quiet: true });
    } catch (requestError) {
      setFormError(requestError?.message || "Sentiment could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProjectOverlay
      className="new-hrm-project-workspace-dialog"
      loading={loading || saving}
      loadingLabel={saving ? "Saving project sentiment" : "Loading project workspace"}
      onClose={onClose}
      size="large"
      title={projectName}
    >
      {error && !detail ? (
        <div className="new-hrm-project-workspace-error" role="alert">
          <FiActivity aria-hidden="true" />
          <div><strong>Project workspace unavailable</strong><span>{error}</span></div>
          <button type="button" className="new-hrm-secondary-button" onClick={() => loadProject()}>Try again</button>
        </div>
      ) : (
        <div className="new-hrm-project-workspace-detail" style={{ "--project-color": projectColor }}>
          <section className="new-hrm-project-workspace-hero">
            <div>
              <span className="new-hrm-project-workspace-hero__eyebrow"><i aria-hidden="true" />{projectCode || "Active project"}</span>
              <h3>{projectName}</h3>
              <p>{stripProjectRichText(firstProjectValue(project, ["description"], "")) || "Operational project workspace."}</p>
            </div>
            <dl>
              <div><dt>Status</dt><dd>{firstProjectValue(project, ["status"], "Active")}</dd></div>
              <div><dt>Account</dt><dd>{accountName}</dd></div>
              <div><dt>Project manager</dt><dd>{managerName}</dd></div>
            </dl>
          </section>

          <section className="new-hrm-project-workspace-kpis" aria-label="Project summary">
            <article><FiClock aria-hidden="true" /><span>Logged hours</span><strong>{formatProjectHours(loggedHours)}</strong></article>
            <article><FiCheckCircle aria-hidden="true" /><span>Approved hours</span><strong>{formatProjectHours(approvedHours)}</strong></article>
            <article><FiBriefcase aria-hidden="true" /><span>Entities</span><strong>{firstProjectValue(summary, ["entityCount", "totalEntities"], entities.length)}</strong></article>
            <article><FiUsers aria-hidden="true" /><span>Team members</span><strong>{firstProjectValue(summary, ["teamMemberCount", "teamCount", "teamSize"], team.length)}</strong></article>
          </section>

          <div className="new-hrm-project-workspace-analytics">
            <section className="new-hrm-project-workspace-section">
              <header><div><FiTrendingUp aria-hidden="true" /><span><strong>Hours trend</strong><small>Logged hours by month</small></span></div></header>
              {hoursByMonth.length ? (
                <div className="new-hrm-project-hours-chart">
                  {hoursByMonth.map((row, index) => {
                    const hours = toFiniteNumber(firstProjectValue(row, ["hours", "loggedHours", "totalHours", "value"]));
                    const label = firstProjectValue(row, ["label", "month", "period"], `Period ${index + 1}`);
                    return (
                      <div key={`${label}-${index}`}>
                        <span>{label}</span>
                        <i><b style={{ width: `${Math.max((hours / maxMonthlyHours) * 100, hours ? 4 : 0)}%` }} /></i>
                        <strong>{formatProjectHours(hours)}</strong>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="new-hrm-empty-state">No logged-hour trend is available yet.</p>}
            </section>

            <section className="new-hrm-project-workspace-section">
              <header><div><FiBarChart2 aria-hidden="true" /><span><strong>Entity status</strong><small>Current delivery distribution</small></span></div></header>
              {statusRows.length ? (
                <div className="new-hrm-project-status-chart">
                  {statusRows.map((row) => (
                    <div key={row.label}>
                      <span>{row.label}</span>
                      <i><b style={{ width: `${Math.max((row.value / maxStatusCount) * 100, row.value ? 4 : 0)}%` }} /></i>
                      <strong>{row.value}</strong>
                    </div>
                  ))}
                </div>
              ) : <p className="new-hrm-empty-state">No project entities are available yet.</p>}
            </section>
          </div>

          <section className="new-hrm-project-workspace-section">
            <header><div><FiUsers aria-hidden="true" /><span><strong>Project team</strong><small>Allocations and contribution</small></span></div><em>{team.length}</em></header>
            {team.length ? (
              <div className="new-hrm-project-workspace-table-wrap">
                <table className="new-hrm-project-workspace-table">
                  <thead><tr><th>Team member</th><th>Role</th><th>Allocation</th><th>Logged</th><th>Entities</th></tr></thead>
                  <tbody>{team.map((member, index) => {
                    const memberName = firstProjectValue(member, ["name", "employeeName", "assignedTo"], "Team member");
                    return (
                      <tr key={firstProjectValue(member, ["id", "contactId", "employeeId"], `${memberName}-${index}`)}>
                        <td data-label="Team member"><span className="new-hrm-avatar new-hrm-avatar--small">{getInitials(memberName)}</span><strong>{memberName}</strong></td>
                        <td data-label="Role">{firstProjectValue(member, ["role", "allocationRole", "designation"], "Contributor")}</td>
                        <td data-label="Allocation">{firstProjectValue(member, ["allocation", "allocationPercent", "percentage"], "Active")}</td>
                        <td data-label="Logged">{formatProjectHours(firstProjectValue(member, ["loggedHours", "totalHours", "hours"], 0))}</td>
                        <td data-label="Entities">{firstProjectValue(member, ["entityCount", "assignedEntityCount", "tasks"], 0)}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            ) : <p className="new-hrm-empty-state">No active project allocations were returned.</p>}
          </section>

          <section className="new-hrm-project-workspace-section">
            <header><div><FiBriefcase aria-hidden="true" /><span><strong>All entities</strong><small>Current project delivery</small></span></div><em>{entities.length}</em></header>
            {entities.length ? (
              <div className="new-hrm-project-workspace-table-wrap">
                <table className="new-hrm-project-workspace-table new-hrm-project-workspace-table--entities">
                  <thead><tr><th>Entity</th><th>Status</th><th>Priority</th><th>Assignee</th><th>Hours</th><th>Due</th></tr></thead>
                  <tbody>{entities.map((entity, index) => (
                    <tr key={firstProjectValue(entity, ["id", "entityId", "key"], index)}>
                      <td data-label="Entity"><strong>{entity.title || entity.name || "Untitled entity"}</strong><small>{entity.key || entity.code || ""}</small></td>
                      <td data-label="Status"><em className={`new-hrm-project-status new-hrm-project-status--${toTone(entity.status)}`}>{entity.status || "Defined"}</em></td>
                      <td data-label="Priority"><em className={`new-hrm-project-priority new-hrm-project-priority--${toTone(entity.priority)}`}>{entity.priority || "Medium"}</em></td>
                      <td data-label="Assignee">{entity.assignedTo || entity.assigneeName || "Unassigned"}</td>
                      <td data-label="Hours">{formatProjectHours(entity.loggedHours || entity.actualHours || 0)}</td>
                      <td data-label="Due">{entity.dueDate || "Not set"}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            ) : <p className="new-hrm-empty-state">No project entities were returned.</p>}
          </section>

          {enableSentiments && <section className="new-hrm-project-workspace-section new-hrm-project-sentiments">
            <header><div><FiMessageCircle aria-hidden="true" /><span><strong>Stakeholder sentiment</strong><small>Latest signals and supporting context</small></span></div><em>{sentiments.length}</em></header>
            <div className={`new-hrm-project-sentiments__layout ${canAddSentiment ? "" : "new-hrm-project-sentiments__layout--readonly"}`}>
              {canAddSentiment && (
                <form className="new-hrm-project-sentiment-form" onSubmit={submitSentiment}>
                  <fieldset disabled={saving}>
                    <legend>Record sentiment</legend>
                    <div className="new-hrm-project-sentiment-control" role="group" aria-label="Sentiment">
                      {["Green", "Amber", "Red"].map((tone) => (
                        <button key={tone} type="button" className={form.sentiment === tone ? "is-active" : ""} data-tone={tone.toLowerCase()} onClick={() => setForm((current) => ({ ...current, sentiment: tone }))}>
                          <i aria-hidden="true" />{tone}
                        </button>
                      ))}
                    </div>
                    <label><span>Stakeholder</span><input value={form.stakeholder} onChange={(event) => { setForm((current) => ({ ...current, stakeholder: event.target.value })); setFormError(""); }} placeholder="Name or stakeholder group" required /></label>
                    <div className="new-hrm-project-sentiment-form__description"><span>Description</span><RichTextEditor ariaLabel="Sentiment description" assignees={assignees} disabled={saving} minHeight={132} onChange={(description) => { setForm((current) => ({ ...current, description })); setFormError(""); }} placeholder="Add the evidence, context, and next action." value={form.description} /></div>
                    {formError && <p className="new-hrm-project-sentiment-form__error" role="alert">{formError}</p>}
                    <button type="submit" className="new-hrm-primary-button" disabled={!canSaveSentiment}>Save sentiment</button>
                  </fieldset>
                </form>
              )}
              <div className="new-hrm-project-sentiment-timeline">
                {sentiments.length ? sentiments.map((item, index) => {
                  const tone = sentimentTone(firstProjectValue(item, ["sentiment", "status", "tone", "value"], "Green"));
                  const stakeholder = firstProjectValue(item, ["stakeholder", "stakeholderName", "source"], "Stakeholder");
                  return (
                    <article key={firstProjectValue(item, ["id", "sentimentId"], `${stakeholder}-${index}`)} data-tone={tone.toLowerCase()}>
                      <i aria-hidden="true" />
                      <div><header><span><strong>{tone}</strong><em>{stakeholder}</em></span><time>{formatProjectDateTime(firstProjectValue(item, ["createdAt", "recordedAt", "date", "updatedAt"]))}</time></header><ProjectRichText value={firstProjectValue(item, ["description", "notes", "comment"], "")} emptyText="No context was recorded." /><small>Recorded by {firstProjectValue(item, ["recordedByName", "createdByName", "createdBy", "authorName", "author"], "Project team")}</small></div>
                    </article>
                  );
                }) : <p className="new-hrm-empty-state">No stakeholder sentiment has been recorded.</p>}
              </div>
            </div>
          </section>}
          {error && <p className="new-hrm-project-workspace-inline-error" role="alert">{error}</p>}
        </div>
      )}
    </ProjectOverlay>
  );
};

export const CommentComposer = ({
  accept = "image/*",
  assignees,
  attachmentAriaLabel = "Attach images",
  attachmentLabel = "Attach",
  compact = false,
  disabled,
  emptyAttachmentBody = "Added image attachment.",
  onSubmit,
  placeholder = "Add a comment. Type @ to mention someone.",
}) => {
  const fileInputRef = useRef(null);
  const [comment, setComment] = useState("");
  const [mentions, setMentions] = useState([]);
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isDisabled = disabled || isSubmitting;

  const addFiles = (fileList) => {
    const nextFiles = Array.from(fileList || []);
    if (!nextFiles.length) {
      return;
    }
    setFiles((current) => [...current, ...nextFiles]);
  };

  const handlePaste = (event) => {
    const pastedImages = imageFilesFromClipboard(event, "project-comment-image");
    if (!pastedImages.length) {
      return;
    }
    event.preventDefault();
    addFiles(pastedImages);
  };

  const removeFile = (fileIndex) => {
    setFiles((current) => current.filter((_, index) => index !== fileIndex));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isDisabled || (!stripProjectRichText(comment).trim() && !files.length)) {
      return;
    }
    const body = stripProjectRichText(comment).trim() ? comment.trim() : emptyAttachmentBody;
    const selectedMentions = mentionsFromRichText(body, assignees, mentions);
    setIsSubmitting(true);
    try {
      const didSubmit = await onSubmit(body, selectedMentions, files);
      if (didSubmit !== false) {
        setComment("");
        setMentions([]);
        setFiles([]);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className={`new-hrm-project-comment-form ${compact ? "new-hrm-project-comment-form--compact" : ""}`}
      onSubmit={handleSubmit}
      aria-busy={isSubmitting}
      data-project-comment-draft={!!stripProjectRichText(comment).trim() || files.length > 0}
    >
      <div className="new-hrm-project-comment-field">
        <RichTextEditor
          assignees={assignees}
          disabled={isDisabled}
          mentions={mentions}
          minHeight={compact ? 58 : 128}
          onChange={setComment}
          onMentionsChange={setMentions}
          onPaste={handlePaste}
          placeholder={placeholder}
          value={comment}
        />
        {files.length > 0 && (
          <div className="new-hrm-project-file-list new-hrm-project-file-list--compact">
            {files.map((file, index) => (
              <span key={`${file.name}-${file.size}-${index}`}>
                {file.type?.startsWith("image/") ? <FiImage aria-hidden="true" /> : <FiFileText aria-hidden="true" />}
                <strong>{file.name}</strong>
                <small>{formatProjectFileSize(file.size)}</small>
                <button type="button" onClick={() => removeFile(index)} disabled={isDisabled} aria-label={`Remove ${file.name}`}>
                  <FiX aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="new-hrm-project-comment-actions">
        <input
          ref={fileInputRef}
          type="file"
          accept={accept || undefined}
          multiple
          onChange={(event) => {
            addFiles(event.target.files);
            event.target.value = "";
          }}
          disabled={isDisabled}
          aria-label={attachmentAriaLabel}
        />
        <div className="new-hrm-project-comment-actions__left">
          <button type="button" className="new-hrm-project-comment-attach" onClick={() => fileInputRef.current?.click()} disabled={isDisabled} title={attachmentLabel}>
            <FiPaperclip aria-hidden="true" />
            {attachmentLabel}
          </button>
          {files.length > 0 && <span>{files.length} file{files.length === 1 ? "" : "s"} ready</span>}
        </div>
        <div className="new-hrm-project-comment-actions__right">
          <button
            type="submit"
            className="new-hrm-primary-button"
            disabled={isDisabled || (!stripProjectRichText(comment).trim() && !files.length)}
            aria-label={isSubmitting ? "Sending comment" : (compact ? "Send" : "Comment")}
          >
            {isSubmitting && <FiLoader className="new-hrm-inline-spinner" aria-hidden="true" />}
            {isSubmitting ? "Sending" : (compact ? "Send" : "Comment")}
          </button>
        </div>
      </div>
    </form>
  );
};

const todayIsoDate = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const EntityLogTimeModal = ({ entity, isSaving, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    workDate: todayIsoDate(),
    hours: "1",
    description: "",
  });

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSaving) {
      return;
    }
    const didSave = await onSubmit(entity, {
      workDate: form.workDate,
      hours: form.hours,
      description: form.description,
      project: entity.projectName || "",
      projectId: entity.projectId || "",
      task: entity.title || entity.key || "",
      taskId: entity.id || "",
    });
    if (didSave !== false) {
      onClose();
    }
  };

  return (
    <ProjectOverlay loading={isSaving} loadingLabel="Saving time entry" title="Log Time" onClose={onClose}>
      <form className="new-hrm-project-form new-hrm-project-time-form" onSubmit={handleSubmit} aria-busy={isSaving}>
        <div className="new-hrm-project-time-context">
          <span>{entity.key}</span>
          <strong>{entity.title}</strong>
          <small>{entity.projectName}</small>
        </div>
        <label>
          <span>Date</span>
          <input type="date" value={form.workDate} onChange={(event) => updateField("workDate", event.target.value)} required disabled={isSaving} />
        </label>
        <label>
          <span>Hours</span>
          <input type="number" min="0.25" max="8" step="0.25" value={form.hours} onChange={(event) => updateField("hours", event.target.value)} required disabled={isSaving} />
        </label>
        <label className="new-hrm-project-form__full">
          <span>Description</span>
          <textarea
            rows={5}
            value={form.description}
            onChange={(event) => updateField("description", event.target.value)}
            placeholder="What was done on this entity?"
            required
            disabled={isSaving}
          />
        </label>
        <div className="new-hrm-project-form__actions">
          <button type="button" className="new-hrm-secondary-button" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button type="submit" className="new-hrm-primary-button" disabled={isSaving || !form.description.trim()}>
            {isSaving ? "Saving..." : "Save Time"}
          </button>
        </div>
      </form>
    </ProjectOverlay>
  );
};

const ProjectDrawer = ({
  assignees,
  currentEmployee,
  displayMode = "drawer",
  entity,
  isSaving,
  onAttachFiles,
  onChecklistAdd,
  onChecklistUpdate,
  onClose,
  onComment,
  onExpand,
  onFileDownload,
  onLogTime,
  onNext,
  onPrevious,
  onProjectOpen,
  savingLabel = "Saving",
  showBlockingLoader = isSaving,
  onStatusChange,
  onUpdate,
  enableTimeLogging = true,
  entityLinkBuilder = buildProjectEntityLink,
  statusOptions = STATUS_OPTIONS,
  preserveDrafts = false,
  requireAssignee = false,
}) => {
  const fileInputRef = useRef(null);
  const [details, setDetails] = useState({
    title: "",
    assignedToId: "",
    priority: "Medium",
    estimatedHours: "",
    dueDate: "",
    description: "",
    descriptionMentions: [],
  });
  const [copyState, setCopyState] = useState("");
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLogTimeOpen, setIsLogTimeOpen] = useState(false);
  const [checklistDraft, setChecklistDraft] = useState("");
  const [checklistOperation, setChecklistOperation] = useState(null);
  const headerMenuRef = useRef(null);
  const lastDraftEntityId = useRef(null);
  const hasEditDraft = useRef(false);

  // Hydrate before paint so a completed detail read cannot reset the next user edit.
  useLayoutEffect(() => {
    const sameEntity = lastDraftEntityId.current === entity?.id;
    lastDraftEntityId.current = entity?.id;
    if (!preserveDrafts || !sameEntity || !hasEditDraft.current) {
      setDetails({
      title: entity?.title || "",
      assignedToId: entity?.assignedToId || "",
      priority: entity?.priority || "Medium",
      estimatedHours: entity?.estimatedHours || "",
      dueDate: /^\d{4}-\d{2}-\d{2}$/.test(String(entity?.dueDate || "")) ? entity.dueDate : "",
      description: entity?.description || "",
      descriptionMentions: [],
      });
      hasEditDraft.current = false;
      setIsEditing(false);
    }
    setCopyState("");
    setIsHeaderMenuOpen(false);
    setIsLogTimeOpen(false);
    if (!preserveDrafts || !sameEntity) setChecklistDraft("");
  }, [entity, preserveDrafts]);

  useEffect(() => {
    if (!isHeaderMenuOpen) {
      return undefined;
    }
    const closeMenu = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") {
        return;
      }
      if (event.type === "pointerdown" && headerMenuRef.current?.contains(event.target)) {
        return;
      }
      setIsHeaderMenuOpen(false);
    };
    document.addEventListener("pointerdown", closeMenu);
    document.addEventListener("keydown", closeMenu);
    return () => {
      document.removeEventListener("pointerdown", closeMenu);
      document.removeEventListener("keydown", closeMenu);
    };
  }, [isHeaderMenuOpen]);

  if (!entity) {
    return null;
  }

  const isExpanded = displayMode === "expanded";
  const canManage = Boolean(entity.isProjectManager);
  const canLogTime = enableTimeLogging && entityOwnedByPerson(entity, currentEmployee);
  const isClosed = normalizeText(entity.status) === "closed";
  const checklist = Array.isArray(entity.checklist) ? entity.checklist : [];
  const completedChecklistCount = checklist.filter((item) => item.completed).length;
  const checklistAvailable = entity.checklistAvailable !== false;
  const activeChecklistOperation = checklistOperation?.entityId === entity.id ? checklistOperation : null;
  const isChecklistBusy = Boolean(activeChecklistOperation);
  const updateField = (field, value) => setDetails((current) => ({ ...current, [field]: value }));
  const selectedAssignee = assignees.find((person) => personMatchesValue(person, details.assignedToId));

  const attachFiles = async (fileList) => {
    const selectedFiles = Array.from(fileList || []);
    if (!selectedFiles.length || isSaving) {
      return;
    }
    await onAttachFiles(entity.id, selectedFiles);
  };

  const handleDrawerPaste = (event) => {
    if (event.defaultPrevented) {
      return;
    }
    const pastedImages = imageFilesFromClipboard(event, "project-entity-attachment");
    if (!pastedImages.length) {
      return;
    }
    event.preventDefault();
    attachFiles(pastedImages);
  };

  const handleSaveDetails = async () => {
    const descriptionMentions = mentionsFromRichText(details.description, assignees, details.descriptionMentions);
    const didSave = await onUpdate(entity.id, {
      ...details,
      descriptionMentions,
      mentions: descriptionMentions,
      assignedTo: selectedAssignee?.name || entity.assignedTo,
    });
    if (didSave !== false) {
      hasEditDraft.current = false;
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    hasEditDraft.current = false;
    setDetails({
      title: entity.title || "",
      assignedToId: entity.assignedToId || "",
      priority: entity.priority || "Medium",
      estimatedHours: entity.estimatedHours || "",
      dueDate: /^\d{4}-\d{2}-\d{2}$/.test(String(entity.dueDate || "")) ? entity.dueDate : "",
      description: entity.description || "",
      descriptionMentions: [],
    });
    setIsEditing(false);
  };

  const handleChecklistSubmit = async (event) => {
    event.preventDefault();
    const label = checklistDraft.trim();
    if (!label || isSaving || isChecklistBusy) {
      return;
    }
    setChecklistOperation({ entityId: entity.id, type: "add" });
    try {
      const didSave = await onChecklistAdd(entity.id, label);
      if (didSave !== false) {
        setChecklistDraft("");
      }
    } finally {
      setChecklistOperation(null);
    }
  };

  const handleChecklistUpdate = async (item, payload, type) => {
    if (isSaving || isChecklistBusy) {
      return;
    }
    setChecklistOperation({ entityId: entity.id, itemId: item.id, type });
    try {
      await onChecklistUpdate(entity.id, item.id, payload);
    } finally {
      setChecklistOperation(null);
    }
  };

  const handleCopyEntityLink = async () => {
    const link = entityLinkBuilder?.(entity);
    if (!link) {
      return;
    }
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = link;
        textArea.setAttribute("readonly", "true");
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(textArea);
        if (!copied) throw new Error("Clipboard unavailable");
      }
      setCopyState("Copied");
      window.setTimeout?.(() => setCopyState(""), 1800);
    } catch (error) {
      setCopyState("Copy failed");
      window.setTimeout?.(() => setCopyState(""), 1800);
    }
  };

  return (
    <div className={`new-hrm-project-drawer-backdrop ${isExpanded ? "new-hrm-project-drawer-backdrop--expanded" : ""}`} onClick={onClose}>
      <aside
        className={`new-hrm-project-drawer new-hrm-loader-host ${isExpanded ? "new-hrm-project-drawer--expanded" : ""}`}
        style={{ "--project-color": normalizeProjectColor(entity.projectColor) }}
        aria-busy={isSaving}
        aria-label="Project entity detail"
        data-project-entity-id={entity.id}
        data-project-has-detail-draft={isEditing || !!checklistDraft.trim()}
        onClick={(event) => event.stopPropagation()}
        onPaste={handleDrawerPaste}
      >
        <header className="new-hrm-project-drawer__header">
          <div className="new-hrm-project-drawer__utility-left">
            <button type="button" className="new-hrm-icon-button" onClick={onClose} aria-label="Close project detail" title="Close">
              <FiX aria-hidden="true" />
            </button>
            <div className="new-hrm-project-drawer__breadcrumb">
              <button
                type="button"
                className="new-hrm-project-drawer__project-link"
                onClick={() => onProjectOpen(entity.projectId)}
              >
                {entity.projectName || "Project"}
              </button>
              <span className="new-hrm-project-drawer__separator" aria-hidden="true">/</span>
              <strong title={entity.key}>{entity.key}</strong>
            </div>
          </div>
          <div className="new-hrm-project-drawer__header-actions">
            {entityLinkBuilder && <button type="button" className="new-hrm-icon-button" onClick={handleCopyEntityLink}
              title={copyState || "Copy ticket link"} aria-label={copyState || "Copy ticket link"}><FiCopy aria-hidden="true" /></button>}
            {onPrevious && (
              <button type="button" className="new-hrm-icon-button" onClick={onPrevious} aria-label="Previous project entity" title="Previous">
                <FiChevronLeft aria-hidden="true" />
              </button>
            )}
            {onNext && (
              <button type="button" className="new-hrm-icon-button" onClick={onNext} aria-label="Next project entity" title="Next">
                <FiChevronRight aria-hidden="true" />
              </button>
            )}
            <div className="new-hrm-project-drawer__action-menu" ref={headerMenuRef}>
              <button
                type="button"
                className="new-hrm-icon-button"
                onClick={() => setIsHeaderMenuOpen((current) => !current)}
                aria-expanded={isHeaderMenuOpen}
                aria-haspopup="menu"
                aria-label="More project actions"
                title="More actions"
              >
                <FiMoreHorizontal aria-hidden="true" />
              </button>
              {isHeaderMenuOpen && (
                <div className="new-hrm-project-drawer__action-menu-popover" role="menu">
                  {entityLinkBuilder && <button type="button" role="menuitem" onClick={handleCopyEntityLink}>
                    <FiCopy aria-hidden="true" />
                    <span>{copyState === "Copied" ? "Link copied" : copyState === "Copy failed" ? "Copy failed" : "Copy link"}</span>
                  </button>}
                  {!isExpanded && onExpand && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setIsHeaderMenuOpen(false);
                        onExpand(entity.id);
                      }}
                    >
                      <FiMaximize2 aria-hidden="true" />
                      <span>Open full view</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="new-hrm-project-drawer__body">
          <section className="new-hrm-project-drawer__hero">
            <div className="new-hrm-project-drawer__badges">
              <span>{entity.recordType || "Task"}</span>
              <span className={`new-hrm-project-status new-hrm-project-status--${toTone(entity.status)}`}>{entity.status || "Defined"}</span>
              <span className={`new-hrm-project-priority new-hrm-project-priority--${toTone(entity.priority)}`}>{entity.priority || "Medium"}</span>
            </div>
            {isEditing ? (
              <label className="new-hrm-project-drawer__title-field">
                <span>Title</span>
                <input value={details.title} onChange={(event) => updateField("title", event.target.value)} disabled={isSaving} />
              </label>
            ) : (
              <h2>{entity.title}</h2>
            )}
            <p>{entity.key} · Assigned to {entity.assignedTo || "Unassigned"}{entity.dueDate ? ` · Due ${entity.dueDate}` : ""}</p>
            <div className="new-hrm-project-drawer__actions">
              <button
                type="button"
                className="new-hrm-primary-button"
                onClick={() => onStatusChange(entity.id, isClosed ? "In Progress" : "Closed")}
                disabled={isSaving}
              >
                <FiCheck aria-hidden="true" />
                {isClosed ? "Reopen" : "Mark complete"}
              </button>
              {canLogTime && (
                <button type="button" className="new-hrm-secondary-button" onClick={() => setIsLogTimeOpen(true)} disabled={isSaving}>
                  <FiClock aria-hidden="true" />
                  Log time
                </button>
              )}
              {canManage && !isEditing && (
                <button type="button" className="new-hrm-secondary-button" onClick={() => { hasEditDraft.current = true; setIsEditing(true); }} disabled={isSaving || isClosed}>
                  <FiEdit3 aria-hidden="true" />
                  Edit
                </button>
              )}
              {isEditing && (
                <>
                  <button type="button" className="new-hrm-primary-button" onClick={handleSaveDetails} disabled={isSaving || !details.title.trim()}>Save</button>
                  <button type="button" className="new-hrm-secondary-button" onClick={handleCancelEdit} disabled={isSaving}>Cancel</button>
                </>
              )}
            </div>
          </section>

          <div className="new-hrm-project-drawer__content">
            <main className="new-hrm-project-drawer__main">
              <section className="new-hrm-project-drawer__section new-hrm-project-drawer__description" data-project-description>
                <div className="new-hrm-project-drawer__section-title">
                  <h3>Description</h3>
                  <small>{isEditing ? "Rich text and @mentions enabled" : "Scope and context"}</small>
                </div>
                {isEditing ? (
                <RichDescriptionField
                  assignees={assignees}
                  mentions={details.descriptionMentions}
                  onMentionsChange={(mentions) => updateField("descriptionMentions", mentions)}
                  value={details.description}
                  onChange={(value) => updateField("description", value)}
                  disabled={isSaving}
                  placeholder="Update entity details. Type @ to tag someone."
                />
                ) : (
                  <ProjectRichText value={entity.description} emptyText="No description has been added yet." />
                )}
              </section>

              <section className="new-hrm-project-drawer__section new-hrm-project-checklist">
                <div className="new-hrm-project-drawer__section-title">
                  <h3>Checklist</h3>
                  {checklistAvailable && <small>{completedChecklistCount} of {checklist.length} complete</small>}
                </div>
                {!checklistAvailable && <p className="new-hrm-project-checklist__unavailable" role="status">{entity.checklistUnavailableReason || "Checklists are currently unavailable for this project."}</p>}
                {checklistAvailable && checklist.length > 0 && (
                  <div className="new-hrm-project-checklist__items">
                    {checklist.map((item) => {
                      const itemOperation = activeChecklistOperation?.itemId === item.id ? activeChecklistOperation : null;
                      return (
                        <div
                          key={item.id}
                          className={`${item.completed ? "is-complete" : ""}${itemOperation ? " is-updating" : ""}`}
                          aria-busy={Boolean(itemOperation)}
                        >
                          <label>
                            <input
                              type="checkbox"
                              checked={Boolean(item.completed)}
                              onChange={() => handleChecklistUpdate(item, { completed: !item.completed }, "update")}
                              disabled={isSaving || isChecklistBusy}
                            />
                            <span>{item.label}</span>
                          </label>
                          {itemOperation ? (
                            <span className="new-hrm-project-checklist__loader" role="status" aria-label={`${itemOperation.type === "remove" ? "Removing" : "Saving"} ${item.label}`}>
                              <FiLoader className="new-hrm-inline-spinner" aria-hidden="true" />
                              {itemOperation.type === "remove" ? "Removing" : "Saving"}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleChecklistUpdate(item, { active: false }, "remove")}
                              disabled={isSaving || isChecklistBusy}
                              aria-label={`Remove ${item.label}`}
                              title="Remove checklist item"
                            >
                              <FiTrash2 aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {checklistAvailable && <form className="new-hrm-project-checklist__add" onSubmit={handleChecklistSubmit} aria-busy={activeChecklistOperation?.type === "add"}>
                  <input
                    value={checklistDraft}
                    onChange={(event) => setChecklistDraft(event.target.value)}
                    placeholder="Add a checklist item"
                    aria-label="New checklist item"
                    maxLength={255}
                    disabled={isSaving || isChecklistBusy}
                  />
                  <button type="submit" className="new-hrm-secondary-button" disabled={isSaving || isChecklistBusy || !checklistDraft.trim()}>
                    {activeChecklistOperation?.type === "add" ? <FiLoader className="new-hrm-inline-spinner" aria-hidden="true" /> : <FiPlus aria-hidden="true" />}
                    {activeChecklistOperation?.type === "add" ? "Adding" : "Add"}
                  </button>
                </form>}
              </section>

              <section className="new-hrm-project-drawer__section new-hrm-project-attachments">
                <div className="new-hrm-project-drawer__section-title">
                  <h3>Files</h3>
                  <button type="button" className="new-hrm-secondary-button new-hrm-project-attachment-action" onClick={() => fileInputRef.current?.click()} disabled={isSaving}>
                    <FiPaperclip aria-hidden="true" />
                    Attach
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(event) => {
                    attachFiles(event.target.files);
                    event.target.value = "";
                  }}
                  disabled={isSaving}
                />
                {entity.attachments?.length > 0 ? (
                  <div className="new-hrm-project-attachments__list">
                    {entity.attachments.map((file) => (
                      <button key={file.id || file.fileId || file.name} type="button" onClick={() => onFileDownload(file)} disabled={isSaving}>
                        <FiFileText aria-hidden="true" />
                        <span>{file.name || "Project file"}</span>
                        <small>{file.createdAt || file.contentType || "Download"}</small>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="new-hrm-empty-state">No files attached yet.</p>
                )}
              </section>

              <section className="new-hrm-project-drawer__section new-hrm-project-comments">
                <div className="new-hrm-project-drawer__section-title">
                  <h3>Activity</h3>
                  <small>{entity.comments?.length || 0} update{entity.comments?.length === 1 ? "" : "s"}</small>
                </div>
                <div className="new-hrm-project-comments__list">
                  {entity.comments?.length ? (
                    entity.comments.map((item) => (
                      <article key={item.id} data-project-comment-id={item.id}>
                        <span className="new-hrm-avatar new-hrm-avatar--small">{getInitials(item.author)}</span>
                        <div>
                          <strong>{item.author || "Team member"}</strong>
                          <small>{item.createdAt || "Recently"}</small>
                          <ProjectCommentBody value={item.body} mentions={item.mentions || []} attachments={item.attachments || []} />
                        </div>
                      </article>
                    ))
                  ) : (
                    <p className="new-hrm-empty-state">No activity yet.</p>
                  )}
                </div>
              </section>
            </main>

            <aside className="new-hrm-project-drawer__meta" aria-label="Project entity metadata">
              <label className="new-hrm-project-drawer__field">
                <span>Status</span>
                <select aria-label="Status" value={entity.status} onChange={(event) => onStatusChange(entity.id, event.target.value)} disabled={isSaving}>
                  {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </label>
              <div className="new-hrm-project-drawer__field">
                <span>Assignee</span>
                {isEditing ? (
                  <select aria-label="Assignee" value={details.assignedToId} onChange={(event) => updateField("assignedToId", event.target.value)} disabled={isSaving}>
                    <option value="" disabled={requireAssignee}>{requireAssignee ? "Choose a project member" : "Unassigned"}</option>
                    {assignees.map((person) => <option key={person.id || person.email || person.name} value={getPersonRecordId(person)}>{person.name}</option>)}
                  </select>
                ) : <strong>{entity.assignedTo || "Unassigned"}</strong>}
              </div>
              <div className="new-hrm-project-drawer__field">
                <span>Priority</span>
                {isEditing ? (
                  <select aria-label="Priority" value={details.priority} onChange={(event) => updateField("priority", event.target.value)} disabled={isSaving}>
                    {PRIORITY_OPTIONS.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                  </select>
                ) : <strong>{entity.priority || "Medium"}</strong>}
              </div>
              <div className="new-hrm-project-drawer__field">
                <span>Estimate</span>
                {isEditing ? (
                  <input aria-label="Estimated hours" type="number" min="0" step="0.5" value={details.estimatedHours} onChange={(event) => updateField("estimatedHours", event.target.value)} disabled={isSaving} />
                ) : <strong>{entity.estimatedHours ? `${entity.estimatedHours}h` : "Not set"}</strong>}
              </div>
              <div className="new-hrm-project-drawer__field">
                <span>Due</span>
                {isEditing ? (
                  <input aria-label="Due date" type="date" value={details.dueDate} onChange={(event) => updateField("dueDate", event.target.value)} disabled={isSaving} />
                ) : <strong>{entity.dueDate || "Not set"}</strong>}
              </div>
              <div className="new-hrm-project-drawer__field">
                <span>Logged</span>
                <strong>{entity.actualHours ? `${entity.actualHours}h` : "0h"}</strong>
              </div>
              <div className="new-hrm-project-drawer__field">
                <span>Project</span>
                <button type="button" onClick={() => onProjectOpen(entity.projectId)}>{entity.projectName || "Project"}</button>
              </div>
              <div className="new-hrm-project-drawer__field">
                <span>Manager</span>
                <strong>{entity.projectManagerName || "Not set"}</strong>
              </div>
              <div className="new-hrm-project-drawer__field">
                <span>Reporter</span>
                <strong>{entity.reporter || "Not set"}</strong>
              </div>
              <div className="new-hrm-project-drawer__field">
                <span>Reference</span>
                <strong>{entity.key}</strong>
              </div>
            </aside>
          </div>
        </div>

        <footer className="new-hrm-project-drawer__composer">
          <CommentComposer
            key={entity.id}
            assignees={assignees}
            compact
            disabled={isSaving}
            onSubmit={(body, mentions, files) => onComment(entity.id, body, mentions, files)}
          />
        </footer>
        {enableTimeLogging && isLogTimeOpen && (
          <EntityLogTimeModal
            entity={entity}
            isSaving={isSaving}
            onClose={() => setIsLogTimeOpen(false)}
            onSubmit={onLogTime}
          />
        )}
        <ComponentLoader active={showBlockingLoader && !isLogTimeOpen} label={savingLabel} />
      </aside>
    </div>
  );
};

const ProjectsScreen = ({
  assignees = [],
  canCreateAnyProjectEntity = false,
  canCreateProject = false,
  defaultAssignee = null,
  entities = [],
  focusEntityId = "",
  focusEntityRequest,
  loading = false,
  loadingLabel = "Refreshing project entities",
  onCreateProject,
  onCreateProjectEntity,
  onAccountSearch,
  onEntityAttachFiles = () => false,
  onEntityChecklistAdd = () => false,
  onEntityChecklistUpdate = () => false,
  onEntityComment = () => false,
  onEntityFileDownload = () => false,
  onEntityFiltersChange,
  onEntityLogTime = () => false,
  onEntityStatusChange = () => false,
  onEntityUpdate = () => false,
  operationLoading = "",
  projects = [],
  viewUserId = "",
  projectDetailApi = getProjectDetail,
  projectSentimentApi = addProjectSentiment,
  enableSentiments = true,
  enableTimeLogging = true,
  syncProjectUrl = true,
  fixedProjectId = "",
  entityLinkBuilder = buildProjectEntityLink,
  onEntityOpen,
  initialViewMode = "kanban",
  title = "Projects",
  statusOptions = STATUS_OPTIONS,
  recordTypes = [],
  preserveEntityDrafts = false,
  requireAssignee = false,
}) => {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [query, setQuery] = useState("");
  const [projectFilters, setProjectFilters] = useState([]);
  const [assigneeFilters, setAssigneeFilters] = useState([]);
  const [priorityFilters, setPriorityFilters] = useState([]);
  const [monthFilters, setMonthFilters] = useState([]);
  const [activeDialog, setActiveDialog] = useState("");
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const [expandedEntityId, setExpandedEntityId] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    if (!syncProjectUrl || typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search || "").get("projectId") || "";
  });
  const [draggedEntityId, setDraggedEntityId] = useState("");
  const lastEntityFilterKeyRef = useRef("");
  const lastDefaultAssigneeValueRef = useRef("");
  const lastFocusedEntityIdRef = useRef("");

  const activeProjects = useMemo(() => projects.filter(isActiveProject), [projects]);
  const activeProjectIds = useMemo(() => activeProjects.map((project) => String(project.id)), [activeProjects]);
  const selectedProjectValues = useMemo(() => toFilterValues(projectFilters), [projectFilters]);
  const areAllActiveProjectsSelected =
    activeProjectIds.length > 0 &&
    selectedProjectValues.length >= activeProjectIds.length &&
    activeProjectIds.every((projectId) => selectedProjectValues.includes(projectId));
  const effectiveProjectFilters = useMemo(
    () => (areAllActiveProjectsSelected ? [] : projectFilters),
    [areAllActiveProjectsSelected, projectFilters]
  );
  const singleSelectedProject = useMemo(
    () => projectFilters.length === 1
      ? activeProjects.find((project) => String(project.id) === String(projectFilters[0])) || null
      : null,
    [activeProjects, projectFilters]
  );

  const openProjectWorkspace = useCallback((projectId) => {
    const nextProjectId = String(projectId || "").trim();
    if (!nextProjectId || (fixedProjectId && nextProjectId !== String(fixedProjectId))) return;
    setSelectedEntityId("");
    setExpandedEntityId("");
    setSelectedProjectId(nextProjectId);
    if (syncProjectUrl && typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", "projects");
      url.searchParams.set("projectId", nextProjectId);
      ["entityId", "projectEntityId", "ticketId"].forEach((parameter) => url.searchParams.delete(parameter));
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
  }, [fixedProjectId, syncProjectUrl]);

  const closeProjectWorkspace = useCallback(() => {
    setSelectedProjectId("");
    if (syncProjectUrl && typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("projectId");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
  }, [syncProjectUrl]);

  useEffect(() => {
    if (!syncProjectUrl || typeof window === "undefined") return undefined;
    const syncProjectFromUrl = () => {
      setSelectedProjectId(new URLSearchParams(window.location.search || "").get("projectId") || "");
    };
    window.addEventListener("popstate", syncProjectFromUrl);
    return () => window.removeEventListener("popstate", syncProjectFromUrl);
  }, [syncProjectUrl]);
  const projectManagedInCurrentContext = useCallback((project = {}) => {
    if (canCreateAnyProjectEntity) {
      return true;
    }
    return projectManagedByPerson(project, defaultAssignee);
  }, [canCreateAnyProjectEntity, defaultAssignee]);
  const contextProjectManagerProjects = useMemo(
    () => activeProjects.filter(projectManagedInCurrentContext),
    [activeProjects, projectManagedInCurrentContext]
  );
  const enrichedEntities = useMemo(
    () =>
      entities.map((entity) => {
        const project = activeProjects.find((item) => entityMatchesProject(entity, item));
        const entityManagerContext = {
          ...project,
          projectManagerId: entity.projectManagerId || project?.projectManagerId,
          projectManagerContactId: entity.projectManagerContactId || project?.projectManagerContactId,
          managerId: entity.managerId || project?.managerId,
          projectManagerEmail: entity.projectManagerEmail || project?.projectManagerEmail,
          managerEmail: entity.managerEmail || project?.managerEmail,
          projectManagerName: entity.projectManagerName || project?.projectManagerName,
          managerName: entity.managerName || project?.managerName,
        };
        return {
          ...entity,
          isProjectManager: Boolean(
            canCreateAnyProjectEntity ||
              projectManagedByPerson(entityManagerContext, defaultAssignee)
          ),
          projectColor: entity.projectColor || project?.colorCode || project?.projectColor || "",
          projectManagerName: entity.projectManagerName || project?.projectManagerName || "",
        };
      }),
    [activeProjects, canCreateAnyProjectEntity, defaultAssignee, entities]
  );
  const visibleEntities = useMemo(
    () => enrichedEntities.filter(isEntityOnActiveProject),
    [enrichedEntities]
  );
  const selectedManagedProject = effectiveProjectFilters
    .map((projectId) => activeProjects.find((project) => String(project.id) === String(projectId)))
    .find((project) => project && projectManagedInCurrentContext(project));
  const creatableProjects = canCreateAnyProjectEntity ? activeProjects : contextProjectManagerProjects;
  const selectedProjectCanCreate = effectiveProjectFilters.length ? Boolean(selectedManagedProject) : creatableProjects.length > 0;
  const initialEntityProjectId = selectedManagedProject?.id || creatableProjects[0]?.id || "";

  useEffect(() => {
    const activeProjectIds = new Set(activeProjects.map((project) => String(project.id)));
    setProjectFilters((current) => {
      const nextProjectFilters = current.filter((projectId) => activeProjectIds.has(String(projectId)));
      return nextProjectFilters.length === current.length ? current : nextProjectFilters;
    });
  }, [activeProjects]);

  const assigneeOptions = useMemo(() => {
    const directoryByKey = new Map();
    assignees.forEach((person) => {
      getPersonAliases(person).forEach((key) => directoryByKey.set(String(key), person));
    });
    const peopleByKey = new Map();
    const seenPeople = new Set();
    const addPerson = (person = {}) => {
      const key = getPersonRecordId(person) || person.email || person.name;
      const name = person.name || person.email || person.id;
      if (!key || isUnassignedValue(name)) {
        return;
      }
      const aliases = getPersonAliases(person);
      if (aliases.some((alias) => seenPeople.has(alias))) {
        return;
      }
      aliases.forEach((alias) => seenPeople.add(alias));
      peopleByKey.set(String(key), {
        id: key,
        contactId: person.contactId || person.employeeSalesforceId || "",
        employeeSalesforceId: person.employeeSalesforceId || person.contactId || "",
        name,
        email: person.email || "",
      });
    };

    addPerson(defaultAssignee || {});
    assignees.forEach(addPerson);
    visibleEntities.forEach((entity) => {
      const key = entity.assignedToId || entity.assignedToEmail || entity.assignedTo;
      if (key && !isUnassignedValue(key) && !peopleByKey.has(String(key))) {
        const directoryPerson =
          directoryByKey.get(String(key)) ||
          directoryByKey.get(String(entity.assignedToEmail || "")) ||
          directoryByKey.get(String(entity.assignedTo || ""));
        addPerson(directoryPerson || {
          id: key,
          name: entity.assignedTo || key,
          email: entity.assignedToEmail || "",
        });
      }
    });
    return Array.from(peopleByKey.values()).sort((left, right) => left.name.localeCompare(right.name));
  }, [assignees, defaultAssignee, visibleEntities]);

  const monthOptions = useMemo(() => getProjectMonthOptions(visibleEntities), [visibleEntities]);
  const defaultAssigneeValue = useMemo(() => {
    if (!defaultAssignee) {
      return "";
    }

    const defaultAliases = getPersonAliases(defaultAssignee);
    const matchedPerson = assigneeOptions.find((person) =>
      defaultAliases.some((alias) => personMatchesValue(person, alias))
    );

    return String(getAssigneeFilterValue(matchedPerson || defaultAssignee));
  }, [assigneeOptions, defaultAssignee]);

  useEffect(() => {
    if (!defaultAssigneeValue || lastDefaultAssigneeValueRef.current === defaultAssigneeValue) {
      return;
    }

    lastDefaultAssigneeValueRef.current = defaultAssigneeValue;
    setAssigneeFilters([defaultAssigneeValue]);
  }, [defaultAssigneeValue]);

  const selectedAssignees = useMemo(
    () =>
      assigneeFilters.map((value) => {
        const person = assigneeOptions.find((item) => personMatchesValue(item, value));
        return {
          value,
          id: person?.contactId || person?.employeeSalesforceId || value,
          name: person?.name || "",
          email: person?.email || "",
        };
      }),
    [assigneeFilters, assigneeOptions]
  );
  const selectedAssigneeIdKey = useMemo(() => filterKeyFromValues(selectedAssignees.map((person) => person.id)), [selectedAssignees]);
  const selectedAssigneeNameKey = useMemo(() => filterKeyFromValues(selectedAssignees.map((person) => person.name)), [selectedAssignees]);
  const selectedAssigneeEmailKey = useMemo(() => filterKeyFromValues(selectedAssignees.map((person) => person.email)), [selectedAssignees]);
  const projectFilterKey = useMemo(() => filterKeyFromValues(effectiveProjectFilters), [effectiveProjectFilters]);
  const priorityFilterKey = useMemo(() => filterKeyFromValues(priorityFilters), [priorityFilters]);
  const monthFilterKey = useMemo(() => filterKeyFromValues(monthFilters), [monthFilters]);

  useEffect(() => {
    if (!onEntityFiltersChange) {
      return undefined;
    }

    const nextFilters = {
      assignedToEmails: toFilterValues(selectedAssigneeEmailKey.split("|")),
      assignedToIds: toFilterValues(selectedAssigneeIdKey.split("|")),
      assignedToNames: toFilterValues(selectedAssigneeNameKey.split("|")),
      months: toFilterValues(monthFilters),
      priorities: toFilterValues(priorityFilters),
      projectIds: toFilterValues(effectiveProjectFilters),
      search: query.trim(),
    };
    const filterKey = JSON.stringify(nextFilters);

    if (
      !lastEntityFilterKeyRef.current &&
      !nextFilters.assignedToIds.length &&
      !nextFilters.months.length &&
      !nextFilters.priorities.length &&
      !nextFilters.projectIds.length &&
      !nextFilters.search
    ) {
      lastEntityFilterKeyRef.current = filterKey;
      return undefined;
    }

    if (lastEntityFilterKeyRef.current === filterKey) {
      return undefined;
    }

    lastEntityFilterKeyRef.current = filterKey;

    const timer = window.setTimeout(() => {
      onEntityFiltersChange(nextFilters);
    }, query.trim() ? 300 : 0);

    return () => window.clearTimeout(timer);
  }, [effectiveProjectFilters, monthFilterKey, monthFilters, onEntityFiltersChange, priorityFilterKey, priorityFilters, projectFilterKey, query, selectedAssigneeEmailKey, selectedAssigneeIdKey, selectedAssigneeNameKey]);

  const filteredEntities = useMemo(() => {
    const normalizedQuery = normalizeText(query);
    return visibleEntities.filter((entity) => {
      const matchesSearch =
        !normalizedQuery ||
        [entity.title, entity.key, entity.projectName, entity.assignedTo, entity.status, entity.priority]
          .filter(Boolean)
          .some((value) => normalizeText(value).includes(normalizedQuery));
      const matchesProject = projectMatchesAnyEntity(entity, effectiveProjectFilters, activeProjects);
      const matchesMonth = monthMatchesAnyEntity(entity, monthFilters);
      const matchesAssignee = assigneeMatchesAnyEntity(entity, selectedAssignees);
      const matchesPriority = priorityMatchesAnyEntity(entity, priorityFilters);
      return matchesSearch && matchesProject && matchesAssignee && matchesPriority && matchesMonth;
    });
  }, [activeProjects, effectiveProjectFilters, monthFilters, priorityFilters, query, selectedAssignees, visibleEntities]);

  const summaryCounts = useMemo(
    () => ({
      assigned: filteredEntities.length,
      inProgress: filteredEntities.filter((entity) => getColumnId(entity.status) === "progress").length,
      managed: filteredEntities.filter((entity) => entity.isProjectManager).length,
    }),
    [filteredEntities]
  );

  const selectedEntity = useMemo(
    () => visibleEntities.find((entity) => entity.id === selectedEntityId) || null,
    [selectedEntityId, visibleEntities]
  );
  const expandedEntity = useMemo(
    () => visibleEntities.find((entity) => entity.id === expandedEntityId) || null,
    [expandedEntityId, visibleEntities]
  );
  useEffect(() => {
    const entityId = expandedEntityId || selectedEntityId;
    if (entityId) onEntityOpen?.(entityId);
  }, [expandedEntityId, onEntityOpen, selectedEntityId]);
  const selectedEntityIndex = selectedEntity ? visibleEntities.findIndex((entity) => entity.id === selectedEntity.id) : -1;
  const expandedEntityIndex = expandedEntity ? visibleEntities.findIndex((entity) => entity.id === expandedEntity.id) : -1;
  const selectedWorkspaceProject = useMemo(() => {
    const activeProject = activeProjects.find((project) => String(project.id) === String(selectedProjectId));
    if (activeProject) return activeProject;
    const projectEntity = visibleEntities.find((entity) => String(entity.projectId) === String(selectedProjectId));
    return selectedProjectId ? {
      id: selectedProjectId,
      name: projectEntity?.projectName || "Project workspace",
      colorCode: projectEntity?.projectColor || "",
      projectManagerName: projectEntity?.projectManagerName || "",
    } : {};
  }, [activeProjects, selectedProjectId, visibleEntities]);

  useEffect(() => {
    const requestedEntityId = String(focusEntityId || "").trim();
    const requestKey = focusEntityRequest || requestedEntityId;
    if (!requestedEntityId || lastFocusedEntityIdRef.current === requestKey) {
      return;
    }
    const matchedEntity = visibleEntities.find((entity) =>
      [entity.id, entity.key, entity.entityId, entity.taskId].filter(Boolean).map(String).includes(requestedEntityId)
    );
    if (!matchedEntity) {
      return;
    }
    setSelectedEntityId(matchedEntity.id);
    lastFocusedEntityIdRef.current = requestKey;
  }, [focusEntityId, focusEntityRequest, visibleEntities]);

  const groupedEntities = useMemo(
    () =>
      STATUS_COLUMNS.reduce((groups, column) => {
        groups[column.id] = filteredEntities.filter((entity) => getColumnId(entity.status) === column.id);
        return groups;
      }, {}),
    [filteredEntities]
  );

  const handleDragStart = (event, entityId) => {
    setDraggedEntityId(entityId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", entityId);
  };

  const handleDrop = (event, column) => {
    event.preventDefault();
    const entityId = event.dataTransfer.getData("text/plain") || draggedEntityId;
    setDraggedEntityId("");
    if (entityId) {
      onEntityStatusChange(entityId, column.status);
    }
  };

  const savingEntityId = operationLoading?.startsWith("project-save:") ? operationLoading.split(":")[1] : "";
  const activeDetailEntityId = expandedEntityId || selectedEntityId;
  const isDrawerSaving =
    operationLoading === "project-comment" ||
    operationLoading === "project-file-download" ||
    operationLoading === "project-file-upload" ||
    operationLoading === "project-time-log" ||
    operationLoading?.startsWith("project-checklist:") ||
    (activeDetailEntityId && operationLoading === `project-save:${activeDetailEntityId}`);
  const showDrawerBlockingLoader =
    operationLoading === "project-file-download" ||
    operationLoading === "project-file-upload" ||
    operationLoading === "project-time-log" ||
    (activeDetailEntityId && operationLoading === `project-save:${activeDetailEntityId}`);
  const isProjectSaving = operationLoading === "project-create";
  const isEntitySaving = operationLoading === "project-entity-create";
  const projectOperationLoadingLabel =
    operationLoading === "project-create"
      ? "Creating project"
      : operationLoading === "project-entity-create"
        ? "Creating project entity"
        : operationLoading === "project-comment"
          ? "Saving comment"
          : operationLoading === "project-file-download"
            ? "Preparing file"
            : operationLoading === "project-file-upload"
              ? "Uploading file"
            : operationLoading === "project-time-log"
              ? "Logging time"
              : operationLoading?.startsWith("project-checklist:")
                ? "Updating checklist"
              : operationLoading?.startsWith("project-save:")
                  ? "Saving project entity"
                  : loadingLabel;
  const projectFilterOptions = activeProjects.map((project) => ({
    value: String(project.id),
    label: `${project.name}${project.isProjectManager ? " - PM" : ""}`,
    color: project.colorCode || project.projectColor || "",
  }));
  const assigneeFilterOptions = assigneeOptions.map((person) => ({
    value: String(getAssigneeFilterValue(person)),
    label: person.name,
  }));
  const priorityFilterOptions = PRIORITY_OPTIONS.map((priority) => ({
    value: priority,
    label: priority,
  }));
  const monthFilterOptions = monthOptions.map((month) => ({
    value: month,
    label: formatMonthLabel(month),
  }));

  return (
    <section className="new-hrm-screen new-hrm-projects-screen">
      <ScreenHeader
        eyebrow={fixedProjectId ? "" : "Work"}
        title={title}
        description={fixedProjectId ? "" : "Team project entities with permissioned project-management controls."}
        actions={
          <>
            <label className="new-hrm-project-search">
              <FiSearch aria-hidden="true" />
              <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search entities" />
            </label>
            {singleSelectedProject && (
              <button
                type="button"
                className="new-hrm-secondary-button new-hrm-project-action-button"
                onClick={() => openProjectWorkspace(singleSelectedProject.id)}
                aria-label="View Project"
                title="View selected project"
              >
                <FiBriefcase aria-hidden="true" />
                View Project
              </button>
            )}
            {canCreateProject && (
              <button
                type="button"
                className="new-hrm-secondary-button new-hrm-project-action-button"
                onClick={() => setActiveDialog("project")}
                aria-label="New Project"
                title="Create project"
              >
                <FiPlus aria-hidden="true" />
                New Project
              </button>
            )}
            {creatableProjects.length > 0 && (
              <button
                type="button"
                className="new-hrm-primary-button new-hrm-project-action-button"
                onClick={() => setActiveDialog("entity")}
                disabled={!selectedProjectCanCreate}
                aria-label="New Entity"
                title={selectedProjectCanCreate ? "Create project entity" : "Only the project manager can create entities"}
              >
                <FiEdit3 aria-hidden="true" />
                New Entity
              </button>
            )}
            <div className="new-hrm-view-toggle" role="group" aria-label="Project view">
              <button type="button" aria-label="Kanban view" title="Kanban view" className={viewMode === "kanban" ? "new-hrm-view-toggle__item--active" : ""} onClick={() => setViewMode("kanban")}>
                <FiColumns aria-hidden="true" />
                Kanban
              </button>
              <button type="button" aria-label="List view" title="List view" className={viewMode === "list" ? "new-hrm-view-toggle__item--active" : ""} onClick={() => setViewMode("list")}>
                <FiList aria-hidden="true" />
                List
              </button>
            </div>
          </>
        }
      />

      <section className="new-hrm-panel new-hrm-project-filters">
        {!fixedProjectId && <MultiSelectFilter
          allLabel="All allocated projects"
          label="Project"
          onChange={setProjectFilters}
          options={projectFilterOptions}
          searchable
          selectedValues={projectFilters}
        />}
        <MultiSelectFilter
          allLabel="Everyone"
          label="Assigned To"
          onChange={setAssigneeFilters}
          options={assigneeFilterOptions}
          searchable
          selectedValues={assigneeFilters}
        />
        <MultiSelectFilter
          allLabel="All priorities"
          label="Priority"
          onChange={setPriorityFilters}
          options={priorityFilterOptions}
          selectedValues={priorityFilters}
        />
        <MultiSelectFilter
          allLabel="All months"
          label="Month"
          onChange={setMonthFilters}
          options={monthFilterOptions}
          searchable
          selectedValues={monthFilters}
        />
        <div className="new-hrm-project-summary" aria-label="Assigned work summary">
          <span><strong>{summaryCounts.assigned}</strong>Visible</span>
          <span><strong>{summaryCounts.inProgress}</strong>In progress</span>
          <span><strong>{summaryCounts.managed}</strong>Managed work</span>
        </div>
      </section>

      <section className="new-hrm-panel new-hrm-loader-host new-hrm-project-workspace" aria-busy={loading || Boolean(operationLoading)}>
        {viewMode === "kanban" ? (
          <div className="new-hrm-project-kanban">
            {STATUS_COLUMNS.map((column) => (
              <section
                key={column.id}
                className="new-hrm-project-kanban__column"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => handleDrop(event, column)}
              >
                <header>
                  <span>{column.label}</span>
                  <strong>{groupedEntities[column.id]?.length || 0}</strong>
                </header>
                <div className="new-hrm-project-kanban__cards">
                  {groupedEntities[column.id]?.length ? (
                    groupedEntities[column.id].map((entity) => (
                      <ProjectEntityCard
                        key={entity.id}
                        entity={entity}
                        isUpdating={savingEntityId === entity.id}
                        onClick={setSelectedEntityId}
                        onDragStart={handleDragStart}
                        onExpand={setExpandedEntityId}
                        onProjectOpen={openProjectWorkspace}
                      />
                    ))
                  ) : (
                    <p className="new-hrm-project-kanban__empty">Drop entities here</p>
                  )}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="new-hrm-project-list">
            <div className="new-hrm-project-list__head">
              <span>Entity</span>
              <span>Project</span>
              <span>Status</span>
              <span>Priority</span>
              <span>Assignee</span>
              <span>Due</span>
            </div>
            {filteredEntities.map((entity) => (
              <div
                key={entity.id}
                className="new-hrm-project-list__row"
                style={{ "--project-color": normalizeProjectColor(entity.projectColor) }}
                onClick={() => setSelectedEntityId(entity.id)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedEntityId(entity.id);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <strong data-label="Entity">{entity.key}<small>{entity.title}</small></strong>
                <button
                  type="button"
                  className="new-hrm-project-list__project"
                  data-label="Project"
                  onClick={(event) => {
                    event.stopPropagation();
                    openProjectWorkspace(entity.projectId);
                  }}
                  aria-label={`Open ${entity.projectName || "project"} project`}
                ><i aria-hidden="true" />{entity.projectName}</button>
                <em data-label="Status" className={`new-hrm-project-status new-hrm-project-status--${toTone(entity.status)}`}>{entity.status}</em>
                <em data-label="Priority" className={`new-hrm-project-priority new-hrm-project-priority--${toTone(entity.priority)}`}>{entity.priority}</em>
                <span data-label="Assignee" className="new-hrm-project-list__person"><FiUser aria-hidden="true" />{entity.assignedTo || "Unassigned"}</span>
                <span data-label="Due">{entity.dueDate || "Not set"}<FiChevronRight aria-hidden="true" /></span>
              </div>
            ))}
          </div>
        )}

        {!filteredEntities.length && !loading && <p className="new-hrm-empty-state">No project entities found.</p>}
        <ComponentLoader
          active={loading || operationLoading === "project-file-download" || operationLoading === "project-file-upload" || operationLoading === "project-time-log" || isProjectSaving || isEntitySaving}
          label={projectOperationLoadingLabel}
        />
      </section>

      <ProjectDrawer
        preserveDrafts={preserveEntityDrafts}
        requireAssignee={requireAssignee}
        statusOptions={statusOptions}
        enableTimeLogging={enableTimeLogging}
        entityLinkBuilder={entityLinkBuilder}
        assignees={assigneeOptions}
        currentEmployee={defaultAssignee}
        entity={expandedEntity ? null : selectedEntity}
        isSaving={Boolean(isDrawerSaving)}
        onAttachFiles={onEntityAttachFiles}
        onChecklistAdd={onEntityChecklistAdd}
        onChecklistUpdate={onEntityChecklistUpdate}
        onClose={() => setSelectedEntityId("")}
        onComment={onEntityComment}
        onExpand={setExpandedEntityId}
        onFileDownload={onEntityFileDownload}
        onLogTime={onEntityLogTime}
        onNext={selectedEntityIndex >= 0 && selectedEntityIndex < visibleEntities.length - 1 ? () => setSelectedEntityId(visibleEntities[selectedEntityIndex + 1].id) : null}
        onPrevious={selectedEntityIndex > 0 ? () => setSelectedEntityId(visibleEntities[selectedEntityIndex - 1].id) : null}
        onProjectOpen={openProjectWorkspace}
        savingLabel={projectOperationLoadingLabel}
        showBlockingLoader={Boolean(showDrawerBlockingLoader)}
        onStatusChange={onEntityStatusChange}
        onUpdate={onEntityUpdate}
      />

      <ProjectDrawer
        preserveDrafts={preserveEntityDrafts}
        requireAssignee={requireAssignee}
        statusOptions={statusOptions}
        enableTimeLogging={enableTimeLogging}
        entityLinkBuilder={entityLinkBuilder}
        assignees={assigneeOptions}
        currentEmployee={defaultAssignee}
        displayMode="expanded"
        entity={expandedEntity}
        isSaving={Boolean(isDrawerSaving)}
        onAttachFiles={onEntityAttachFiles}
        onChecklistAdd={onEntityChecklistAdd}
        onChecklistUpdate={onEntityChecklistUpdate}
        onClose={() => setExpandedEntityId("")}
        onComment={onEntityComment}
        onFileDownload={onEntityFileDownload}
        onLogTime={onEntityLogTime}
        onNext={expandedEntityIndex >= 0 && expandedEntityIndex < visibleEntities.length - 1 ? () => setExpandedEntityId(visibleEntities[expandedEntityIndex + 1].id) : null}
        onPrevious={expandedEntityIndex > 0 ? () => setExpandedEntityId(visibleEntities[expandedEntityIndex - 1].id) : null}
        onProjectOpen={openProjectWorkspace}
        savingLabel={projectOperationLoadingLabel}
        showBlockingLoader={Boolean(showDrawerBlockingLoader)}
        onStatusChange={onEntityStatusChange}
        onUpdate={onEntityUpdate}
      />

      {activeDialog === "project" && (
        <ProjectModal assignees={assigneeOptions} isSaving={isProjectSaving} onAccountSearch={onAccountSearch} onClose={() => setActiveDialog("")} onCreate={onCreateProject} />
      )}
      {activeDialog === "entity" && (
        <EntityModal
          statusOptions={statusOptions}
          recordTypes={recordTypes}
          assignees={assigneeOptions}
          initialProjectId={initialEntityProjectId}
          isSaving={isEntitySaving}
          onClose={() => setActiveDialog("")}
          onCreate={onCreateProjectEntity}
          projects={creatableProjects}
        />
      )}
      {selectedProjectId && (
        <ProjectWorkspaceModal
          assignees={assigneeOptions}
          fallbackProject={selectedWorkspaceProject}
          onClose={closeProjectWorkspace}
          projectId={selectedProjectId}
          viewUserId={viewUserId}
          projectDetailApi={projectDetailApi}
          projectSentimentApi={projectSentimentApi}
          enableSentiments={enableSentiments}
        />
      )}
    </section>
  );
};

export default ProjectsScreen;
